import {
        getUserBillingState,
        getUserData,
        getUserNotificationData,
        getTreatments,
        getAllGeneralEvents,
        getWorkers,
        getBioNodeById,
        getNodeConnectivityStatus,
        getSensordata,
        getGeneralEvents,
} from "../database/firestore";
import { getAnimal, getAnimals } from "../api/firestore/services/animalService";
import { getAllPredictions } from "../api/firestore/services/predictionService";
import { getAllNodes } from "../api/firestore/services/sensorService";
import { getBirths } from "../database/animalEvents";
import { getLifecycleEvents } from "../api/firestore/services/calfLifecycleService";
import { getWorkItems, saveWorkItem } from "./workItemApi";
import { getKPIMetrics, getKPITrends } from "../database/kpiService";
import { retrieveRelevantKnowledge } from "./farmManager/knowledgeRetriever";
import { getKnowledgeSection } from "./farmManager/knowledgeBase";
import { getProductionRecords } from "../api/firestore/services/productionService";
import {
        SUSPICIOUS_HEALTH_SCORE_THRESHOLD,
        isAnimalActive,
        getAnimalHealthState,
        requiresAnimalHealthReview,
} from "../utils/animalUtils";
import { getAllLifecycleEvents } from "../api/firestore/services/calfLifecycleService";
import { getAllVaccinations, getOverdueVaccinations, getUpcomingVaccinations } from "../api/firestore/services/vaccinationService";
import { getAllWeightRecords, getWeightRecordsByAnimal, calculateADG, detectWeightAnomalies } from "../api/firestore/services/weightTrackingService";
import { getAllDiseaseRecords, getDiseaseRecordsByAnimal, classifyWisconsinScore } from "../api/firestore/services/diseaseTrackingService";
import { calculateCalfKPIs } from "../api/firestore/services/calfKPIService";

const TOOL_IDS = {
        ACCOUNT_OVERVIEW: "account-overview",
        BILLING_STATUS: "billing-status",
        NOTIFICATION_PREFERENCES: "notification-preferences",
        TREATMENT_HISTORY: "treatment-history",
        BIRTH_HISTORY: "birth-history",
        EVENT_HISTORY: "event-history",
        WORK_PLANNER_OVERVIEW: "work-planner-overview",
        WORK_PLANNER_WORKERS: "work-planner-workers",
        WORK_PLANNER_CREATE_TASK: "work-planner-create-task",
        BIONODE_STATUS: "bionode-status",
        BIONODE_ANALYSIS: "bionode-analysis",
        ANIMAL_DETAILS: "animal-details",
        NAVIGATE_PAGE: "navigate-page",
        NAVIGATE_ANIMAL: "navigate-animal",
        FARM_KPI_OVERVIEW: "farm-kpi-overview",
        FARM_KPI_TRENDS: "farm-kpi-trends",
        SEARCH_KNOWLEDGE_BASE: "search-knowledge-base",
        PRODUCTION_HISTORY: "production-history",
        // Calf-specific tools
        CALF_LIFECYCLE_OVERVIEW: "calf-lifecycle-overview",
        VACCINATION_STATUS: "vaccination-status",
        WEIGHT_GROWTH_ANALYSIS: "weight-growth-analysis",
        DISEASE_OVERVIEW: "disease-overview",
        CALF_HEALTH_KPIS: "calf-health-kpis",
        // Diagnostic and analysis tools
        HEALTH_DIAGNOSIS_ASSIST: "health-diagnosis-assist",
        COMPARATIVE_ANALYSIS: "comparative-analysis",
        GENERATE_REPORT: "generate-report",
        ACTIVITY_ANOMALY_ANALYSIS: "activity-anomaly-analysis",
        FEEDING_ANALYSIS: "feeding-analysis",
};

const ALLOWED_TASK_STATUSES = new Set(["pending", "in-progress", "completed", "cancelled"]);
const ALLOWED_TASK_TYPES = ["Work", "Health", "Treatment", "General"];
const DEFAULT_TASK_DURATION_MINUTES = 60;
const ALLOWED_NAVIGATION_PATHS = new Set([
        "/",
        "/dashboard",
        "/animals",
        "/animal",
        "/archive",
        "/barn",
        "/barn-section",
        "/planner-ai",
        "/predictions",
        "/settings",
        "/treatments",
        "/workplaner",
        "/biotdashboard",
        "/erp",
]);

export const CHAT_TOOLS = {
        [TOOL_IDS.ACCOUNT_OVERVIEW]: {
                id: TOOL_IDS.ACCOUNT_OVERVIEW,
                labelKey: "ai-chat-tool-account-overview",
                descriptionKey: "ai-chat-tool-account-overview-desc",
        },
        [TOOL_IDS.BILLING_STATUS]: {
                id: TOOL_IDS.BILLING_STATUS,
                labelKey: "ai-chat-tool-billing-status",
                descriptionKey: "ai-chat-tool-billing-status-desc",
        },
        [TOOL_IDS.NOTIFICATION_PREFERENCES]: {
                id: TOOL_IDS.NOTIFICATION_PREFERENCES,
                labelKey: "ai-chat-tool-notification-preferences",
                descriptionKey: "ai-chat-tool-notification-preferences-desc",
        },
        [TOOL_IDS.TREATMENT_HISTORY]: {
                id: TOOL_IDS.TREATMENT_HISTORY,
                labelKey: "ai-chat-tool-treatment-history",
                descriptionKey: "ai-chat-tool-treatment-history-desc",
        },
        [TOOL_IDS.BIRTH_HISTORY]: {
                id: TOOL_IDS.BIRTH_HISTORY,
                labelKey: "ai-chat-tool-birth-history",
                descriptionKey: "ai-chat-tool-birth-history-desc",
        },
        [TOOL_IDS.EVENT_HISTORY]: {
                id: TOOL_IDS.EVENT_HISTORY,
                labelKey: "ai-chat-tool-event-history",
                descriptionKey: "ai-chat-tool-event-history-desc",
        },
        [TOOL_IDS.SEARCH_KNOWLEDGE_BASE]: {
                id: TOOL_IDS.SEARCH_KNOWLEDGE_BASE,
                labelKey: "ai-chat-tool-search-knowledge-base",
                descriptionKey: "ai-chat-tool-search-knowledge-base-desc",
        },
        [TOOL_IDS.PRODUCTION_HISTORY]: {
                id: TOOL_IDS.PRODUCTION_HISTORY,
                labelKey: "ai-chat-tool-production-history",
                descriptionKey: "ai-chat-tool-production-history-desc",
        },
        [TOOL_IDS.WORK_PLANNER_OVERVIEW]: {
                id: TOOL_IDS.WORK_PLANNER_OVERVIEW,
                labelKey: "ai-chat-tool-work-planner-overview",
                descriptionKey: "ai-chat-tool-work-planner-overview-desc",
        },
        [TOOL_IDS.WORK_PLANNER_WORKERS]: {
                id: TOOL_IDS.WORK_PLANNER_WORKERS,
                labelKey: "ai-chat-tool-work-planner-workers",
                descriptionKey: "ai-chat-tool-work-planner-workers-desc",
        },
        [TOOL_IDS.WORK_PLANNER_CREATE_TASK]: {
                id: TOOL_IDS.WORK_PLANNER_CREATE_TASK,
                labelKey: "ai-chat-tool-work-planner-create-task",
                descriptionKey: "ai-chat-tool-work-planner-create-task-desc",
        },
        [TOOL_IDS.BIONODE_STATUS]: {
                id: TOOL_IDS.BIONODE_STATUS,
                labelKey: "ai-chat-tool-bionode-status",
                descriptionKey: "ai-chat-tool-bionode-status-desc",
        },
        [TOOL_IDS.BIONODE_ANALYSIS]: {
                id: TOOL_IDS.BIONODE_ANALYSIS,
                labelKey: "ai-chat-tool-bionode-analysis",
                descriptionKey: "ai-chat-tool-bionode-analysis-desc",
        },
        [TOOL_IDS.ANIMAL_DETAILS]: {
                id: TOOL_IDS.ANIMAL_DETAILS,
                labelKey: "ai-chat-tool-animal-details",
                descriptionKey: "ai-chat-tool-animal-details-desc",
        },
        [TOOL_IDS.NAVIGATE_PAGE]: {
                id: TOOL_IDS.NAVIGATE_PAGE,
                labelKey: "ai-chat-tool-navigate-page",
                descriptionKey: "ai-chat-tool-navigate-page-desc",
        },
        [TOOL_IDS.NAVIGATE_ANIMAL]: {
                id: TOOL_IDS.NAVIGATE_ANIMAL,
                labelKey: "ai-chat-tool-navigate-animal",
                descriptionKey: "ai-chat-tool-navigate-animal-desc",
        },
        [TOOL_IDS.CALF_LIFECYCLE_OVERVIEW]: {
                id: TOOL_IDS.CALF_LIFECYCLE_OVERVIEW,
                labelKey: "ai-chat-tool-calf-lifecycle-overview",
                descriptionKey: "ai-chat-tool-calf-lifecycle-overview-desc",
        },
        [TOOL_IDS.VACCINATION_STATUS]: {
                id: TOOL_IDS.VACCINATION_STATUS,
                labelKey: "ai-chat-tool-vaccination-status",
                descriptionKey: "ai-chat-tool-vaccination-status-desc",
        },
        [TOOL_IDS.WEIGHT_GROWTH_ANALYSIS]: {
                id: TOOL_IDS.WEIGHT_GROWTH_ANALYSIS,
                labelKey: "ai-chat-tool-weight-growth-analysis",
                descriptionKey: "ai-chat-tool-weight-growth-analysis-desc",
        },
        [TOOL_IDS.DISEASE_OVERVIEW]: {
                id: TOOL_IDS.DISEASE_OVERVIEW,
                labelKey: "ai-chat-tool-disease-overview",
                descriptionKey: "ai-chat-tool-disease-overview-desc",
        },
        [TOOL_IDS.CALF_HEALTH_KPIS]: {
                id: TOOL_IDS.CALF_HEALTH_KPIS,
                labelKey: "ai-chat-tool-calf-health-kpis",
                descriptionKey: "ai-chat-tool-calf-health-kpis-desc",
        },
        [TOOL_IDS.FARM_KPI_OVERVIEW]: {
                id: TOOL_IDS.FARM_KPI_OVERVIEW,
                labelKey: "ai-chat-tool-farm-kpi-overview",
                descriptionKey: "ai-chat-tool-farm-kpi-overview-desc",
        },
        [TOOL_IDS.FARM_KPI_TRENDS]: {
                id: TOOL_IDS.FARM_KPI_TRENDS,
                labelKey: "ai-chat-tool-farm-kpi-trends",
                descriptionKey: "ai-chat-tool-farm-kpi-trends-desc",
        },
        // New diagnostic and analysis tools
        [TOOL_IDS.HEALTH_DIAGNOSIS_ASSIST]: {
                id: TOOL_IDS.HEALTH_DIAGNOSIS_ASSIST,
                labelKey: "ai-chat-tool-health-diagnosis-assist",
                descriptionKey: "ai-chat-tool-health-diagnosis-assist-desc",
        },
        [TOOL_IDS.COMPARATIVE_ANALYSIS]: {
                id: TOOL_IDS.COMPARATIVE_ANALYSIS,
                labelKey: "ai-chat-tool-comparative-analysis",
                descriptionKey: "ai-chat-tool-comparative-analysis-desc",
        },
        [TOOL_IDS.GENERATE_REPORT]: {
                id: TOOL_IDS.GENERATE_REPORT,
                labelKey: "ai-chat-tool-generate-report",
                descriptionKey: "ai-chat-tool-generate-report-desc",
        },
        [TOOL_IDS.ACTIVITY_ANOMALY_ANALYSIS]: {
                id: TOOL_IDS.ACTIVITY_ANOMALY_ANALYSIS,
                labelKey: "ai-chat-tool-activity-anomaly-analysis",
                descriptionKey: "ai-chat-tool-activity-anomaly-analysis-desc",
        },
        [TOOL_IDS.FEEDING_ANALYSIS]: {
                id: TOOL_IDS.FEEDING_ANALYSIS,
                labelKey: "ai-chat-tool-feeding-analysis",
                descriptionKey: "ai-chat-tool-feeding-analysis-desc",
        },
};

/**
 * Formats a Firestore timestamp or numeric value into a localized date string.
 * @param {any} value
 * @param {string} locale
 * @param {Function} t
 * @returns {string}
 */
function formatDate(value, locale, t) {
        if (!value) {
                return t("ai-chat-field-date-not-set");
        }

        let date;

        if (typeof value === "number") {
                date = new Date(value);
        } else if (value instanceof Date) {
                date = value;
        } else if (value?.seconds) {
                date = new Date(value.seconds * 1000);
        } else if (value?.toDate) {
                date = value.toDate();
        } else {
                date = new Date(value);
        }

        if (!date || Number.isNaN(date.getTime())) {
                return t("ai-chat-field-date-not-set");
        }

        return new Intl.DateTimeFormat(locale || "en", {
                dateStyle: "medium",
        }).format(date);
}

/**
 * Converts a value into a Date instance if possible.
 * @param {any} value
 * @returns {Date|null}
 */
function normalizeToDate(value) {
        if (value === undefined || value === null) {
                return null;
        }

        if (value instanceof Date) {
                return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === "number") {
                const date = new Date(value);
                return Number.isNaN(date.getTime()) ? null : date;
        }

        if (typeof value === "string") {
                const numeric = Number(value);
                if (!Number.isNaN(numeric) && value.trim() !== "") {
                        const asNumberDate = new Date(numeric);
                        if (!Number.isNaN(asNumberDate.getTime())) {
                                return asNumberDate;
                        }
                }

                const asDate = new Date(value);
                return Number.isNaN(asDate.getTime()) ? null : asDate;
        }

        if (value?.seconds) {
                const date = new Date(value.seconds * 1000);
                return Number.isNaN(date.getTime()) ? null : date;
        }

        if (typeof value?.toDate === "function") {
                const date = value.toDate();
                return Number.isNaN(date.getTime()) ? null : date;
        }

        return null;
}

/**
 * Determines the primary timestamp for a record, preferring explicit timestamp fields.
 * @param {Record<string, any>} record
 * @returns {number}
 */
function getPrimaryTimestamp(record) {
        if (!record) {
                return 0;
        }

        const candidates = [record.t, record.StartDateSeconds, record.StartDate, record.timestamp];

        for (const candidate of candidates) {
                const date = normalizeToDate(candidate);
                if (date) {
                        return date.getTime();
                }
        }

        return 0;
}

function normalizePayload(params) {
        if (!params || typeof params !== "object" || Array.isArray(params)) {
                return {};
        }

        if (!Object.prototype.hasOwnProperty.call(params, "payload")) {
                return {};
        }

        const value = params.payload;

        if (!value || typeof value === "boolean") {
                return {};
        }

        if (typeof value === "string") {
                try {
                        const parsed = JSON.parse(value);
                        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
                } catch (error) {
                        console.warn("[chatToolsService][normalizePayload] - Failed to parse payload string:", value, error);
                        return {};
                }
        }

        if (typeof value === "object" && !Array.isArray(value)) {
                return value;
        }

        return {};
}

function normalizeNavigationPath(value) {
        if (typeof value !== "string") {
                return null;
        }

        const trimmed = value.trim();

        if (!trimmed) {
                return null;
        }

        const lower = trimmed.toLowerCase();

        if (lower.startsWith("http://") || lower.startsWith("https://") || lower.startsWith("mailto:") || lower.startsWith("tel:")) {
                return null;
        }

        if (!trimmed.startsWith("/")) {
                return null;
        }

        const [basePath] = trimmed.split(/[?#]/);

        if (!ALLOWED_NAVIGATION_PATHS.has(basePath)) {
                return null;
        }

        return trimmed;
}

function formatStatusLabel(status, t) {
        if (!status || typeof status !== "string") {
                return t("ai-chat-field-not-available");
        }

        const normalized = status.trim().toLowerCase();

        if (!normalized) {
                return t("ai-chat-field-not-available");
        }

        return t(`status-${normalized}`, {
                defaultValue: humanizeKey(normalized.replace(/-/g, " ")),
        });
}

function ensureUniqueStringArray(values) {
        if (!Array.isArray(values)) {
                return [];
        }

        const unique = new Set();
        values.forEach((value) => {
                if (value !== undefined && value !== null) {
                        unique.add(String(value));
                }
        });

        return Array.from(unique);
}

/**
 * Builds a response message for list-based data sets.
 * @param {Object} params
 * @param {string} params.title
 * @param {string} params.icon
 * @param {Array} params.items
 * @param {(item: any, index: number) => string} params.formatItem
 * @param {Function} params.t
 * @returns {{ message: string, metadata: { empty: boolean, items: Array } }}
 */
function buildListMessage({ title, icon, items, formatItem, t, summaryLines = [] }) {
        const prefix = icon ? `${icon} ${title}` : title;
        const summaries = Array.isArray(summaryLines) ? summaryLines.filter(Boolean) : [];

        if (!items || items.length === 0) {
                const summarySection = summaries.length > 0 ? `\n${summaries.join("\n")}` : "";

                return {
                        message: `${prefix}${summarySection}\n${t("ai-chat-tool-empty-result")}`,
                        metadata: {
                                empty: true,
                                items: [],
                        },
                };
        }

        const lines = items.map((item, index) => formatItem(item, index)).filter(Boolean);
        const summarySection = summaries.length > 0 ? `\n${summaries.join("\n")}\n\n` : "\n";

        return {
                message: `${prefix}${summarySection}${lines.join("\n")}`,
                metadata: {
                        empty: lines.length === 0,
                        items,
                },
        };
}

export const KPI_FIELD_CONFIG = [
        { key: "mortalityRate", translationKey: "ai-chat-field-mortality-rate", unit: "percent" },
        { key: "averageADG", translationKey: "ai-chat-field-average-adg", unit: "weight" },
        { key: "vaccinationCompliance", translationKey: "ai-chat-field-vaccination-compliance", unit: "percent" },
        { key: "diseaseIncidence", translationKey: "ai-chat-field-disease-incidence", unit: "percent" },
        { key: "scoursRate", translationKey: "ai-chat-field-scours-rate", unit: "percent" },
        { key: "brdRate", translationKey: "ai-chat-field-brd-rate", unit: "percent" },
        { key: "averageWisconsinScore", translationKey: "ai-chat-field-avg-wisconsin-score", unit: null },
        { key: "totalAnimals", translationKey: "ai-chat-field-total-animals", unit: null },
];

const KPI_UNIT_FORMATTERS = {
        percent: (value, locale, t, options = {}) => {
                const formatOptions = {
                        maximumFractionDigits: options.maximumFractionDigits ?? 1,
                        minimumFractionDigits: options.minimumFractionDigits ?? 0,
                };

                if (options.signDisplay) {
                        formatOptions.signDisplay = options.signDisplay;
                }

                const formatter = new Intl.NumberFormat(locale || "en", formatOptions);
                return `${formatter.format(value)}${t("ai-chat-unit-percent")}`;
        },
        days: (value, locale, t, options = {}) => {
                const formatOptions = {
                        maximumFractionDigits: options.maximumFractionDigits ?? 1,
                        minimumFractionDigits: options.minimumFractionDigits ?? 0,
                };

                if (options.signDisplay) {
                        formatOptions.signDisplay = options.signDisplay;
                }

                const formatter = new Intl.NumberFormat(locale || "en", formatOptions);
                return `${formatter.format(value)} ${t("ai-chat-unit-days")}`;
        },
        weight: (value, locale, t, options = {}) => {
                const formatOptions = {
                        maximumFractionDigits: options.maximumFractionDigits ?? 1,
                        minimumFractionDigits: options.minimumFractionDigits ?? 0,
                };

                if (options.signDisplay) {
                        formatOptions.signDisplay = options.signDisplay;
                }

                const formatter = new Intl.NumberFormat(locale || "en", formatOptions);
                return `${formatter.format(value)} ${t("ai-chat-unit-kg")}`;
        },
};

function normalizeLimit(value, { min = 1, max = 10, fallback = 5 } = {}) {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) {
                return fallback;
        }

        return Math.min(Math.max(Math.floor(numeric), min), max);
}

export function formatDateLabel(value, locale, t) {
        if (!value || typeof value !== "string") {
                // Handle Date objects and timestamps
                if (value instanceof Date) {
                        return new Intl.DateTimeFormat(locale || "en", { dateStyle: "medium" }).format(value);
                }
                if (typeof value === "number" && value > 0) {
                        return new Intl.DateTimeFormat(locale || "en", { dateStyle: "medium" }).format(new Date(value));
                }
                return t("ai-chat-field-date-not-set");
        }

        const parsed = new Date(`${value}T00:00:00Z`);
        if (!Number.isNaN(parsed.getTime())) {
                return new Intl.DateTimeFormat(locale || "en", { dateStyle: "medium" }).format(parsed);
        }

        return value;
}

/**
 * Get the title for a report based on report type
 */
function getReportTitle(reportType, t) {
        const titles = {
                "weekly-summary": t("ai-chat-tool-generate-report-weekly", { defaultValue: "Weekly Summary Report" }),
                "health-overview": t("ai-chat-tool-generate-report-health", { defaultValue: "Health Overview Report" }),
                "calf-health": t("ai-chat-tool-generate-report-calf-health", { defaultValue: "Calf Health Report" }),
                "treatment-summary": t("ai-chat-tool-generate-report-treatment", { defaultValue: "Treatment Summary Report" }),
                "vet-prep": t("ai-chat-tool-generate-report-vet", { defaultValue: "Veterinary Visit Preparation" }),
        };
        return titles[reportType] || t("ai-chat-tool-generate-report-title", { defaultValue: "Farm Report" });
}

export function formatPeriodType(value, t) {
        if (!value || typeof value !== "string") {
                return t("ai-chat-field-not-available");
        }

        const normalized = value.toLowerCase();
        return t(`ai-chat-period-${normalized}`, { defaultValue: humanizeKey(normalized) });
}

function resolveKpiUnit(key) {
        switch (key) {
                case "averageBirthWeight":
                case "averageWeaningWeight":
                        return "weight";
                case "averageWeaningAge":
                        return "days";
                default:
                        return KPI_FIELD_CONFIG.find((entry) => entry.key === key)?.unit || null;
        }
}

export function formatKpiValue(key, value, locale, t, { signed = false } = {}) {
        if (value === undefined || value === null) {
                                return t("ai-chat-field-not-available");
        }

        if (typeof value !== "number" || Number.isNaN(value)) {
                return String(value);
        }

        const unit = resolveKpiUnit(key);
        const baseOptions = {
                maximumFractionDigits: Math.abs(value) < 1 ? 2 : 1,
                minimumFractionDigits: 0,
        };

        if (signed) {
                baseOptions.signDisplay = "always";
        }

        if (unit && KPI_UNIT_FORMATTERS[unit]) {
                return KPI_UNIT_FORMATTERS[unit](value, locale, t, baseOptions);
        }

        const formatter = new Intl.NumberFormat(locale || "en", baseOptions);
        return formatter.format(value);
}

function buildKpiMetricLines(metric, locale, t) {
        const lines = [];
        KPI_FIELD_CONFIG.forEach((entry) => {
                if (metric[entry.key] !== undefined && metric[entry.key] !== null) {
                        lines.push(
                                `${t(entry.translationKey)}: ${formatKpiValue(entry.key, metric[entry.key], locale, t)}`
                        );
                }
        });

        const optionalFields = [
                { key: "averageBirthWeight", translationKey: "ai-chat-field-average-birth-weight" },
                { key: "averageWeaningAge", translationKey: "ai-chat-field-average-weaning-age" },
                { key: "averageWeaningWeight", translationKey: "ai-chat-field-average-weaning-weight" },
        ];

        optionalFields.forEach((entry) => {
                if (metric[entry.key] !== undefined && metric[entry.key] !== null) {
                        lines.push(
                                `${t(entry.translationKey)}: ${formatKpiValue(entry.key, metric[entry.key], locale, t)}`
                        );
                }
        });

        return lines;
}

export function summarizeKpiMetric(metric, locale, t) {
        if (!metric) {
                return null;
        }

        const topMetrics = KPI_FIELD_CONFIG
                .map((entry) => {
                        if (metric[entry.key] === undefined || metric[entry.key] === null) {
                                return null;
                        }

                        return `${t(entry.translationKey)} ${formatKpiValue(entry.key, metric[entry.key], locale, t)}`;
                })
                .filter(Boolean)
                .slice(0, 3);

        if (topMetrics.length === 0) {
                return null;
        }

        const dateLabel = formatDateLabel(metric.date, locale, t);
        const periodLabel = formatPeriodType(metric.periodType, t);
        return t("ai-chat-tool-farm-kpi-overview-summary", {
                date: dateLabel,
                period: periodLabel,
                metrics: topMetrics.join(" · "),
        });
}

function formatKpiListItem(metric, index, locale, t) {
        const dateLabel = formatDateLabel(metric.date, locale, t);
        const periodLabel = formatPeriodType(metric.periodType, t);
        const lines = buildKpiMetricLines(metric, locale, t);

        const prefix = `${index + 1}. ${t("ai-chat-field-date")}: ${dateLabel} (${periodLabel})`;
        if (lines.length === 0) {
                return prefix;
        }

        return `${prefix}\n   - ${lines.join("\n   - ")}`;
}

export function formatKpiChange(firstMetric, lastMetric, kpiKey, locale, t) {
        if (!firstMetric || !lastMetric) {
                return null;
        }

        const firstValue = firstMetric[kpiKey] ?? firstMetric.value;
        const lastValue = lastMetric[kpiKey] ?? lastMetric.value;

        if (typeof firstValue !== "number" || typeof lastValue !== "number") {
                return null;
        }

        const delta = lastValue - firstValue;
        if (!Number.isFinite(delta)) {
                return null;
        }

        const directionSymbol = delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
        const formattedChange = formatKpiValue(kpiKey, delta, locale, t, { signed: true });

        return `${t("ai-chat-field-change")}: ${directionSymbol} ${formattedChange}`;
}

export function resolvePrimaryKpi(metric) {
        if (!metric) {
                return null;
        }

        const availableKey = KPI_FIELD_CONFIG.find((entry) =>
                typeof metric[entry.key] === "number"
        );

        return availableKey?.key || null;
}

export function resolveKpiLabel(key, t) {
        if (!key) {
                return t("ai-chat-field-kpi");
        }

        const configEntry = KPI_FIELD_CONFIG.find((entry) => entry.key === key);
        if (configEntry) {
                return t(configEntry.translationKey);
        }

        return t(`ai-chat-field-${key}`, { defaultValue: humanizeKey(key) });
}

/**
 * Calculates the next planned dose date for a treatment.
 * @param {Record<string, any>} treatment
 * @returns {Date|null}
 */
function calculateNextDoseDate(treatment) {
        if (!treatment) {
                return null;
        }

        const interval = Number(treatment.TreatmentInterval);
        if (!interval || Number.isNaN(interval)) {
                return null;
        }

        const history = Array.isArray(treatment.PhoneHistory) ? treatment.PhoneHistory : [];
        const lastHistoryEntry = history.length > 0 ? history[history.length - 1] : null;
        const baseDate = normalizeToDate(lastHistoryEntry?.t || treatment.LastTreatmentDate || treatment.StartDateSeconds || treatment.t);

        if (!baseDate) {
                return null;
        }

        if (!treatment.TreatmentCount || Number(treatment.TreatmentCount) <= 0) {
                return baseDate;
        }

        const nextDate = new Date(baseDate.getTime());
        nextDate.setDate(nextDate.getDate() + interval);
        return nextDate;
}

/**
 * Formats boolean values using localized strings.
 * @param {boolean} value
 * @param {Function} t
 * @returns {string}
 */
function formatBoolean(value, t) {
        if (value === undefined || value === null) {
                return t("ai-chat-field-not-available");
        }
        return value ? t("ai-chat-field-boolean-yes") : t("ai-chat-field-boolean-no");
}

function buildOverviewSummary(tasks, locale, t) {
        if (!Array.isArray(tasks) || tasks.length === 0) {
                return [t("ai-chat-tool-work-planner-overview-empty-summary")];
        }

        const summaryLines = [];
        summaryLines.push(
                t("ai-chat-tool-work-planner-overview-summary-count", {
                        count: tasks.length,
                }),
        );

        const statusCounts = tasks.reduce((acc, task) => {
                const status = typeof task?.status === "string" ? task.status.toLowerCase() : "";
                if (!status) {
                        return acc;
                }

                acc[status] = (acc[status] || 0) + 1;
                return acc;
        }, {});

        const statusSummary = Object.entries(statusCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => `${formatStatusLabel(status, t)} ×${count}`)
                .join(", ");

        if (statusSummary) {
                        summaryLines.push(
                                t("ai-chat-tool-work-planner-overview-summary-statuses", {
                                        statuses: statusSummary,
                                }),
                        );
        }

        let earliestStart = null;
        tasks.forEach((task) => {
                const start = normalizeToDate(task?.startDate ?? task?.t);
                if (!start) {
                        return;
                }

                if (!earliestStart || start < earliestStart) {
                        earliestStart = start;
                }
        });

        if (earliestStart) {
                summaryLines.push(
                        t("ai-chat-tool-work-planner-overview-summary-next", {
                                date: formatDate(earliestStart, locale, t),
                        }),
                );
        }

        const unassignedCount = tasks.filter((task) => {
                const assignedName =
                        task?.assignedToName || task?.assignedTo?.name || task?.assignedWorkerName || "";
                return !assignedName.trim();
        }).length;

        if (unassignedCount > 0) {
                summaryLines.push(
                        t("ai-chat-tool-work-planner-overview-summary-unassigned", {
                                count: unassignedCount,
                        }),
                );
        } else {
                summaryLines.push(t("ai-chat-tool-work-planner-overview-summary-all-assigned"));
        }

        return summaryLines;
}

function buildWorkersSummary(workers, locale, t) {
        if (!Array.isArray(workers) || workers.length === 0) {
                return [t("ai-chat-tool-work-planner-workers-empty-summary")];
        }

        const summaryLines = [];
        summaryLines.push(
                t("ai-chat-tool-work-planner-workers-summary-count", {
                        count: workers.length,
                }),
        );

        const activeCount = workers.filter((worker) => worker?.isActive !== false).length;
        const inactiveCount = workers.length - activeCount;

        summaryLines.push(
                t("ai-chat-tool-work-planner-workers-summary-active", {
                        count: activeCount,
                }),
        );

        summaryLines.push(
                inactiveCount > 0
                        ? t("ai-chat-tool-work-planner-workers-summary-inactive", {
                                  count: inactiveCount,
                          })
                        : t("ai-chat-tool-work-planner-workers-summary-all-active"),
        );

        let mostRecentActivity = null;
        workers.forEach((worker) => {
                const activityDate = normalizeToDate(worker?.lastActivity);
                if (!activityDate) {
                        return;
                }

                if (!mostRecentActivity || activityDate > mostRecentActivity) {
                        mostRecentActivity = activityDate;
                }
        });

        summaryLines.push(
                mostRecentActivity
                        ? t("ai-chat-tool-work-planner-workers-summary-recent", {
                                  date: formatDate(mostRecentActivity, locale, t),
                          })
                        : t("ai-chat-tool-work-planner-workers-summary-no-activity"),
        );

        return summaryLines;
}

/**
 * Formats a duration in milliseconds to a human-readable string
 * @param {number} ms - Duration in milliseconds
 * @returns {string}
 */
function formatDuration(ms) {
        if (!ms || ms < 0) {
                return "0 minutes";
        }

        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
                return `${days} day${days > 1 ? "s" : ""}`;
        }
        if (hours > 0) {
                return `${hours} hour${hours > 1 ? "s" : ""}`;
        }
        if (minutes > 0) {
                return `${minutes} minute${minutes > 1 ? "s" : ""}`;
        }
        return `${seconds} second${seconds > 1 ? "s" : ""}`;
}

/**
 * Generates a status summary for a collection of nodes
 * @param {Array} nodes - Array of node objects
 * @param {Function} t - Translation function
 * @param {string} locale - Locale string
 * @returns {Array<string>} Array of summary lines
 */
function formatNodeStatusSummary(nodes, t, locale) {
        if (!Array.isArray(nodes) || nodes.length === 0) {
                return [t("ai-chat-tool-bionode-status-empty")];
        }

        const summaryLines = [];
        summaryLines.push(
                t("ai-chat-tool-bionode-status-summary-count", {
                        count: nodes.length,
                }),
        );

        const statusCounts = { healthy: 0, warning: 0, critical: 0 };
        const now = Date.now();

        nodes.forEach((node) => {
                const statusInfo = getNodeConnectivityStatus(node, now);
                statusCounts[statusInfo.status] = (statusCounts[statusInfo.status] || 0) + 1;
        });

        if (statusCounts.healthy > 0) {
                summaryLines.push(
                        t("ai-chat-tool-bionode-status-summary-healthy", {
                                count: statusCounts.healthy,
                        }),
                );
        }

        if (statusCounts.warning > 0) {
                summaryLines.push(
                        t("ai-chat-tool-bionode-status-summary-warning", {
                                count: statusCounts.warning,
                        }),
                );
        }

        if (statusCounts.critical > 0) {
                summaryLines.push(
                        t("ai-chat-tool-bionode-status-summary-critical", {
                                count: statusCounts.critical,
                        }),
                );
        }

        // Find most recently active node
        const mostRecentNode = nodes.reduce((latest, node) => {
                if (!latest || (node.t && node.t > (latest.t || 0))) {
                        return node;
                }
                return latest;
        }, null);

        if (mostRecentNode && mostRecentNode.t) {
                summaryLines.push(
                        t("ai-chat-tool-bionode-status-summary-last-activity", {
                                nodeId: mostRecentNode.ID || mostRecentNode.Id || t("ai-chat-field-not-available"),
                                date: formatDate(mostRecentNode.t, locale, t),
                        }),
                );
        }

        return summaryLines;
}

/**
 * Generates troubleshooting guidance based on node status
 * @param {Array} nodes - Array of node objects
 * @param {Function} t - Translation function
 * @returns {Array<string>} Array of troubleshooting recommendations
 */
function generateTroubleshootingGuidance(nodes, t) {
        if (!Array.isArray(nodes) || nodes.length === 0) {
                return [t("ai-chat-tool-bionode-troubleshooting-no-nodes")];
        }

        const now = Date.now();
        const criticalNodes = [];
        const warningNodes = [];

        nodes.forEach((node) => {
                const statusInfo = getNodeConnectivityStatus(node, now);
                if (statusInfo.status === "critical") {
                        criticalNodes.push({
                                node,
                                statusInfo,
                        });
                } else if (statusInfo.status === "warning") {
                        warningNodes.push({
                                node,
                                statusInfo,
                        });
                }
        });

        const recommendations = [];

        if (criticalNodes.length > 0) {
                if (criticalNodes.length === nodes.length) {
                        recommendations.push(t("ai-chat-tool-bionode-troubleshooting-all-offline"));
                        recommendations.push(t("ai-chat-tool-bionode-troubleshooting-check-network"));
                } else {
                        recommendations.push(
                                t("ai-chat-tool-bionode-troubleshooting-some-offline", {
                                        count: criticalNodes.length,
                                }),
                        );
                }

                recommendations.push(t("ai-chat-tool-bionode-troubleshooting-check-power"));
                recommendations.push(t("ai-chat-tool-bionode-troubleshooting-check-connection"));

                if (criticalNodes.length === 1) {
                        const nodeId = criticalNodes[0].node.ID || criticalNodes[0].node.Id || t("ai-chat-field-node");
                        recommendations.push(
                                t("ai-chat-tool-bionode-troubleshooting-specific-node", {
                                        nodeId,
                                }),
                        );
                }
        } else if (warningNodes.length > 0) {
                recommendations.push(
                        t("ai-chat-tool-bionode-troubleshooting-some-warning", {
                                count: warningNodes.length,
                        }),
                );
                recommendations.push(t("ai-chat-tool-bionode-troubleshooting-monitor"));
        } else {
                recommendations.push(t("ai-chat-tool-bionode-troubleshooting-all-healthy"));
        }

        return recommendations;
}

/**
 * Converts camelCase or PascalCase keys into title-cased strings.
 * @param {string} key
 * @returns {string}
 */
function humanizeKey(key) {
        if (!key) {
                return "";
        }

        return key
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .replace(/_/g, " ")
                .replace(/\s+/g, " ")
                .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Builds a response string with title and bullet lines.
 * @param {string} title
 * @param {string[]} lines
 * @param {Function} t
 * @returns {{ message: string, metadata: { fields: Record<string, string> } }}
 */
function buildResponse(title, lines, t) {
        const filteredLines = lines.filter(Boolean);

        if (filteredLines.length === 0) {
                return {
                        message: `${title}\n${t("ai-chat-tool-empty-result")}`,
                        metadata: {
                                fields: {},
                                empty: true,
                        },
                };
        }

        const metadataFields = {};
        filteredLines.forEach(line => {
                const [label, value] = line.split(":").map(part => part.trim());
                if (label && value) {
                        metadataFields[label] = value;
                }
        });

        return {
                message: `${title}\n${filteredLines.join("\n")}`,
                metadata: {
                        fields: metadataFields,
                        empty: false,
                },
        };
}

/**
 * Finds an animal by ID or MAC address
 * @param {string} userId
 * @param {string} animalIdOrMac - Animal ID or MAC address
 * @returns {Promise<Object|null>} Animal object with MAC, or null if not found
 */
async function findAnimalByIdOrMac(userId, animalIdOrMac) {
        if (!animalIdOrMac || typeof animalIdOrMac !== "string") {
                return null;
        }

        const normalized = animalIdOrMac.trim().toLowerCase();

        if (!normalized) {
                return null;
        }

        // Try to get by MAC first (more efficient)
        try {
                const animal = await getAnimal(userId, normalized);
                if (animal && animal.ID) {
                        return animal;
                }
        } catch (error) {
                // Continue to try ID lookup
        }

        // Try to find by ID if MAC lookup didn't work
        try {
                const animals = await getAnimals(userId);
                const match = animals.find(
                        (animal) =>
                                (animal.ID || "").toLowerCase() === normalized ||
                                (animal.MAC || "").toLowerCase() === normalized
                );

                return match || null;
        } catch (error) {
                return null;
        }
}

/**
 * Executes a chat tool and returns formatted response data.
 * @param {string} toolId
 * @param {string} userId
 * @param {Function} t
 * @param {string} locale
 * @returns {Promise<{ message: string, metadata: { toolId: string, fields: Record<string, string>, empty: boolean } }>} Tool response
 */
export async function executeChatTool(toolId, userId, t, locale = "en", params = {}) {
        const payload = normalizePayload(params);
        const isAutoTriggered = params && typeof params === "object" && params.source === "auto";

        switch (toolId) {
                case TOOL_IDS.NAVIGATE_PAGE: {
                        const candidatePath = typeof payload.path === "string"
                                ? payload.path
                                : typeof payload.to === "string"
                                        ? payload.to
                                        : typeof payload.url === "string"
                                                ? payload.url
                                                : null;

                        const normalizedPath = normalizeNavigationPath(candidatePath);
                        const title = `🔗 ${t("ai-chat-tool-navigate-page-title")}`;

                        if (!candidatePath) {
                                return {
                                        message: `${title}\n${t("ai-chat-tool-navigate-page-missing-path")}`,
                                        metadata: {
                                                toolId,
                                                payload,
                                                empty: true,
                                        },
                                };
                        }

                        if (!normalizedPath) {
                                return {
                                        message: `${title}\n${t("ai-chat-tool-navigate-page-unsupported")}`,
                                        metadata: {
                                                toolId,
                                                payload,
                                                empty: true,
                                                destination: candidatePath,
                                        },
                                };
                        }

                        const label = ["label", "page", "name", "displayName"].reduce((acc, key) => {
                                if (acc) {
                                        return acc;
                                }

                                const value = payload[key];
                                return typeof value === "string" && value.trim() ? value.trim() : acc;
                        }, "");

                        const destinationLabel = label || normalizedPath;
                        const baseResponse = buildResponse(
                                title,
                                [`${t("ai-chat-field-destination")}: ${normalizedPath}`],
                                t,
                        );

                        const message = `${baseResponse.message}\n${t("ai-chat-tool-navigate-page-success", { page: destinationLabel })}`.trim();

                        const navigation = {
                                path: normalizedPath,
                        };

                        if (Object.prototype.hasOwnProperty.call(payload, "state") && payload.state !== undefined) {
                                navigation.state = payload.state;
                        }

                        if (payload.replace === true) {
                                navigation.replace = true;
                        }

                        const metadata = {
                                ...baseResponse.metadata,
                                toolId,
                                payload,
                                destination: normalizedPath,
                                empty: false,
                        };

                        if (!isAutoTriggered) {
                                metadata.navigation = navigation;
                        }

                        return {
                                message,
                                metadata,
                        };
                }
                case TOOL_IDS.NAVIGATE_ANIMAL: {
                        const identifier = [
                                payload.animalMac,
                                payload.mac,
                                payload.animalId,
                                payload.id,
                        ].find((value) => typeof value === "string" && value.trim());

                        const title = `🔗 ${t("ai-chat-tool-navigate-animal-title")}`;

                        if (!identifier) {
                                return {
                                        message: `${title}\n${t("ai-chat-tool-animal-details-empty")}`,
                                        metadata: {
                                                toolId,
                                                payload,
                                                empty: true,
                                        },
                                };
                        }

                        const normalizedIdentifier = identifier.trim();
                        const animal = await findAnimalByIdOrMac(userId, normalizedIdentifier);

                        if (!animal || !animal.MAC) {
                                return {
                                        message: `${title}\n${t("ai-chat-tool-animal-details-not-found", { animalId: normalizedIdentifier })}`,
                                        metadata: {
                                                toolId,
                                                payload,
                                                empty: true,
                                                animalId: normalizedIdentifier,
                                        },
                                };
                        }

                        const mac = animal.MAC;
                        const animalId = animal.ID || normalizedIdentifier;
                        const animalName = animal.Name || animalId || mac;

                        const healthScoreValue = (() => {
                                if (typeof animal.HealthScore === "number" && Number.isFinite(animal.HealthScore)) {
                                        return animal.HealthScore;
                                }

                                if (typeof animal.HealthScore === "string" && animal.HealthScore.trim()) {
                                        const parsed = Number(animal.HealthScore);
                                        return Number.isFinite(parsed) ? parsed : null;
                                }

                                return null;
                        })();

                        const infoLines = [
                                animalId ? `${t("ai-chat-field-animal-id")}: ${animalId}` : null,
                                mac ? `${t("ai-chat-field-mac")}: ${mac}` : null,
                                animal.Group ? `${t("ai-chat-field-group")}: ${animal.Group}` : null,
                        ];

                        if (healthScoreValue !== null) {
                                infoLines.push(`${t("ai-chat-field-health-score")}: ${healthScoreValue.toFixed(1)}%`);
                        }

                        const statusText = (() => {
                                if (animal.Active === true || (typeof animal.Active === "string" && animal.Active.toLowerCase() === "sending")) {
                                        return t("ai-chat-field-active");
                                }

                                if (animal.Active === false) {
                                        return t("ai-chat-field-inactive", { defaultValue: t("inactive") });
                                }

                                return null;
                        })();

                        if (statusText) {
                                infoLines.push(`${t("ai-chat-field-status")}: ${statusText}`);
                        }

                        const baseResponse = buildResponse(
                                title,
                                infoLines.filter(Boolean),
                                t,
                        );

                        const summaryParts = [
                                t("ai-chat-tool-navigate-animal-success", { name: animalName }),
                        ];

                        if (
                                healthScoreValue !== null &&
                                healthScoreValue < SUSPICIOUS_HEALTH_SCORE_THRESHOLD
                        ) {
                                summaryParts.push(
                                        t("ai-chat-tool-navigate-animal-low-health", { score: healthScoreValue.toFixed(1) }),
                                );
                        }

                        const message = `${baseResponse.message}\n${summaryParts.join(" ")}`.trim();

                        const navigation = {
                                path: "/animal",
                                state: mac,
                        };

                        if (payload.replace === true) {
                                navigation.replace = true;
                        }

                        const metadata = {
                                ...baseResponse.metadata,
                                toolId,
                                payload,
                                animalId,
                                mac,
                                empty: false,
                        };

                        if (healthScoreValue !== null) {
                                metadata.healthScore = Number(healthScoreValue.toFixed(1));
                        }

                        if (!isAutoTriggered) {
                                metadata.navigation = navigation;
                        }

                        return {
                                message,
                                metadata,
                        };
                }
                case TOOL_IDS.ACCOUNT_OVERVIEW: {
                        const data = (await getUserData(userId)) || {};

                        const messageData = buildResponse(
                                `📇 ${t("ai-chat-tool-account-overview-title")}`,
                                [
                                        `${t("ai-chat-field-name")}: ${data.Name || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-email")}: ${data.Email || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-phone")}: ${data.Phone || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-language")}: ${data.Language || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-role")}: ${data.Role || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-farm-type")}: ${data.Type || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-beta-user")}: ${formatBoolean(data.betaUser, t)}`,
                                        `${t("ai-chat-field-profile-complete")}: ${formatBoolean(data.ProfileComplete, t)}`,
                                        `${t("ai-chat-field-devices")}: ${Array.isArray(data.Devices) ? data.Devices.length : 0}`,
                                ],
                                t,
                        );

                        return {
                                message: messageData.message,
                                metadata: {
                                        ...messageData.metadata,
                                        toolId,
                                        payload,
                                },
                        };
                }
                case TOOL_IDS.WORK_PLANNER_OVERVIEW: {
                        const limit = Math.min(Math.max(Number(payload.limit) || 5, 1), 20);
                        const taskPrefixOnly = payload.taskPrefixOnly !== false;
                        const startDate = normalizeToDate(payload.startDate ?? payload.start ?? payload.from);
                        const endDate = normalizeToDate(payload.endDate ?? payload.end ?? payload.to);

                        const rawStatuses = Array.isArray(payload.statuses)
                                ? payload.statuses
                                : payload.status || payload.state
                                        ? [payload.status || payload.state]
                                        : [];
                        const normalizedStatuses = rawStatuses
                                .map((status) =>
                                        typeof status === "string"
                                                ? status.trim().toLowerCase()
                                                : null
                                )
                                .filter(Boolean);

                        const rawTypes = Array.isArray(payload.types)
                                ? payload.types
                                : payload.type
                                        ? [payload.type]
                                        : [];
                        const normalizedTypes = rawTypes
                                .map((type) =>
                                        typeof type === "string"
                                                ? type.trim().toLowerCase()
                                                : null
                                )
                                .filter(Boolean);

                        const options = {
                                limit,
                                taskPrefixOnly,
                        };

                        if (startDate) {
                                options.startDate = startDate;
                        }

                        if (endDate) {
                                options.endDate = endDate;
                        }

                        if (normalizedStatuses.length > 0 && taskPrefixOnly && normalizedStatuses.length <= 3) {
                                options.statuses = normalizedStatuses;
                        }

                        if (normalizedTypes.length > 0 && taskPrefixOnly && normalizedTypes.length <= 3) {
                                options.types = normalizedTypes.map((type) => {
                                        const match = ALLOWED_TASK_TYPES.find(
                                                (allowed) => allowed.toLowerCase() === type
                                        );
                                        return match ? match : type;
                                });
                        }

                        let tasks = await getWorkItems(userId, options);

                        if (
                                normalizedStatuses.length > 0 &&
                                (!options.statuses || normalizedStatuses.length > 3 || !taskPrefixOnly)
                        ) {
                                const statusSet = new Set(normalizedStatuses);
                                tasks = tasks.filter((task) =>
                                        statusSet.has(String(task?.status || "").toLowerCase())
                                );
                        }

                        if (normalizedTypes.length > 0 && (!options.types || !taskPrefixOnly)) {
                                const typeSet = new Set(normalizedTypes);
                                tasks = tasks.filter((task) =>
                                        typeSet.has(String(task?.type || "").toLowerCase())
                                );
                        }

                        const assignedWorkerId =
                                typeof payload.assignedWorkerId === "string"
                                        ? payload.assignedWorkerId.trim().toLowerCase()
                                        : null;

                        if (assignedWorkerId) {
                                tasks = tasks.filter((task) => {
                                        const candidates = [
                                                task?.assignedTo?.id,
                                                task?.assignedTo?.token,
                                                task?.assignedToName,
                                        ].filter((value) => typeof value === "string");

                                        return candidates.some(
                                                (candidate) => candidate.trim().toLowerCase() === assignedWorkerId
                                        );
                                });
                        }

                        const deviceToken =
                                typeof payload.deviceToken === "string"
                                        ? payload.deviceToken.trim()
                                        : null;

                        if (deviceToken) {
                                tasks = tasks.filter((task) =>
                                        Array.isArray(task?.devices) &&
                                        task.devices.some((device) => String(device).trim() === deviceToken)
                                );
                        }

                        const limitedTasks = tasks.slice(0, limit);

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-work-planner-overview-title"),
                                icon: "🗓️",
                                items: limitedTasks,
                                t,
                                summaryLines: buildOverviewSummary(limitedTasks, locale, t),
                                formatItem: (task, index) => {
                                        const title = task?.title || t("ai-chat-field-not-available");
                                        const start = normalizeToDate(task?.startDate || task?.t);
                                        const startText = formatDate(start, locale, t);
                                        const statusText = formatStatusLabel(task?.status, t);
                                        const assignedName =
                                                task?.assignedToName ||
                                                task?.assignedTo?.name ||
                                                t("ai-chat-field-unassigned");
                                        const location = (task?.location || "").trim();
                                        const locationPart = location
                                                ? ` • ${t("ai-chat-field-location")}: ${location}`
                                                : "";

                                        return `${index + 1}. ${t("ai-chat-field-title")}: ${title} • ${t(
                                                "ai-chat-field-start"
                                        )}: ${startText} • ${t("ai-chat-field-status")}: ${statusText} • ${t(
                                                "ai-chat-field-assigned-worker"
                                        )}: ${assignedName}${locationPart}`;
                                },
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                        payload,
                                },
                        };
                }
                case TOOL_IDS.WORK_PLANNER_WORKERS: {
                        const workers = await getWorkers(userId);

                        workers.sort((a, b) => {
                                const nameA = (a?.name || "").toLowerCase();
                                const nameB = (b?.name || "").toLowerCase();
                                if (nameA < nameB) {
                                        return -1;
                                }
                                if (nameA > nameB) {
                                        return 1;
                                }
                                return 0;
                        });

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-work-planner-workers-title"),
                                icon: "🛠️",
                                items: workers,
                                t,
                                summaryLines: buildWorkersSummary(workers, locale, t),
                                formatItem: (worker, index) => {
                                        const name = worker?.name || t("ai-chat-field-not-available");
                                        const token = formatTokenPreview(worker?.token, t);
                                        const statusText = formatBoolean(worker?.isActive, t);
                                        const lastActivity = worker?.lastActivity
                                                ? formatDate(worker.lastActivity, locale, t)
                                                : t("ai-chat-field-not-available");

                                        return `${index + 1}. ${t("ai-chat-field-name")}: ${name} • ${t(
                                                "ai-chat-field-device-token"
                                        )}: ${token} • ${t("ai-chat-field-status")}: ${statusText} • ${t(
                                                "ai-chat-field-last-activity"
                                        )}: ${lastActivity}`;
                                },
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                        payload,
                                },
                        };
                }
                case TOOL_IDS.WORK_PLANNER_CREATE_TASK: {
                        const title = (payload.title ?? payload.name ?? "").toString().trim();

                        if (!title) {
                                throw new Error(t("ai-chat-tool-work-planner-create-task-missing-title"));
                        }

                        const startDate = normalizeToDate(payload.startDate ?? payload.start ?? payload.t);

                        if (!startDate) {
                                throw new Error(t("ai-chat-tool-work-planner-create-task-missing-start"));
                        }

                        let endDate = normalizeToDate(payload.endDate ?? payload.end ?? null);
                        const rawDuration = Number(payload.durationMinutes ?? payload.duration ?? 0);

                        const durationMinutes = Number.isFinite(rawDuration) && rawDuration > 0
                                ? rawDuration
                                : DEFAULT_TASK_DURATION_MINUTES;

                        if (!endDate) {
                                endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
                        }

                        let status =
                                typeof payload.status === "string"
                                        ? payload.status.trim().toLowerCase()
                                        : "pending";

                        if (!ALLOWED_TASK_STATUSES.has(status)) {
                                status = "pending";
                        }

                        const rawType = typeof payload.type === "string" ? payload.type.trim().toLowerCase() : "work";
                        const type =
                                ALLOWED_TASK_TYPES.find((allowed) => allowed.toLowerCase() === rawType) || "Work";

                        const notes = (payload.notes ?? payload.description ?? "").toString().trim();
                        const location = (payload.location ?? "").toString().trim();
                        const linkedAnimalId = (payload.animalId ?? payload.SowID ?? payload.animalId ?? "").toString().trim();
                        const selectedGroup = (payload.selectedGroup ?? payload.group ?? payload.selectedGroupId ?? "")
                                .toString()
                                .trim();

                        let devices = [];

                        if (Array.isArray(payload.devices)) {
                                devices = ensureUniqueStringArray(payload.devices);
                        } else if (typeof payload.devices === "string") {
                                devices = ensureUniqueStringArray([payload.devices]);
                        } else if (typeof payload.deviceToken === "string") {
                                devices = ensureUniqueStringArray([payload.deviceToken]);
                        }

                        let assignedWorkerId =
                                typeof payload.assignedWorkerId === "string"
                                        ? payload.assignedWorkerId
                                        : typeof payload.assignedTo === "string"
                                                ? payload.assignedTo
                                                : typeof payload.workerId === "string"
                                                        ? payload.workerId
                                                        : null;

                        let assignedTo = null;

                        if (assignedWorkerId && assignedWorkerId.trim() !== "") {
                                const normalizedWorkerId = assignedWorkerId.trim().toLowerCase();
                                const workers = await getWorkers(userId);
                                const workerMatch = workers.find((worker) => {
                                        const candidates = [worker.id, worker.token, worker.name].filter(
                                                (value) => typeof value === "string"
                                        );

                                        return candidates.some(
                                                (candidate) => candidate.trim().toLowerCase() === normalizedWorkerId
                                        );
                                });

                                if (workerMatch) {
                                        assignedTo = {
                                                id: workerMatch.id,
                                                token: workerMatch.token,
                                                name: workerMatch.name,
                                                isActive: workerMatch.isActive !== false,
                                                lastActivity: workerMatch.lastActivity ?? null,
                                        };

                                        if (devices.length === 0) {
                                                devices = ensureUniqueStringArray([workerMatch.token]);
                                        }
                                } else {
                                        const fallbackName =
                                                (payload.assignedWorkerName || assignedWorkerId).toString().trim();
                                        assignedTo = {
                                                id: assignedWorkerId,
                                                token: assignedWorkerId,
                                                name: fallbackName,
                                                isActive: true,
                                                lastActivity: null,
                                        };

                                        if (devices.length === 0) {
                                                devices = ensureUniqueStringArray([assignedWorkerId]);
                                        }
                                }
                        }

                        const taskData = {
                                title,
                                startDate,
                                endDate,
                                type,
                                status,
                                location: location || undefined,
                                notes: notes || undefined,
                                assignedTo: assignedTo || undefined,
                                assignedToName: assignedTo?.name || undefined,
                                SowID: linkedAnimalId || undefined,
                                selectedGroup: selectedGroup || undefined,
                        };

                        if (devices.length > 0) {
                                taskData.devices = devices;
                        }

                        const taskId = await saveWorkItem(userId, taskData);

                        const startText = formatDate(startDate, locale, t);
                        const endText = endDate ? formatDate(endDate, locale, t) : t("ai-chat-field-not-available");
                        const statusText = formatStatusLabel(status, t);
                        const assignedName = assignedTo?.name || t("ai-chat-field-unassigned");

                        const response = buildResponse(
                                `✅ ${t("ai-chat-tool-work-planner-create-task-title")}`,
                                [
                                        `${t("ai-chat-field-task-id")}: ${taskId}`,
                                        `${t("ai-chat-field-title")}: ${title}`,
                                        `${t("ai-chat-field-start")}: ${startText}`,
                                        `${t("ai-chat-field-end")}: ${endText}`,
                                        `${t("ai-chat-field-status")}: ${statusText}`,
                                        `${t("ai-chat-field-assigned-worker")}: ${assignedName}`,
                                        `${t("ai-chat-field-type")}: ${type}`,
                                        location ? `${t("ai-chat-field-location")}: ${location}` : "",
                                        notes ? `${t("ai-chat-field-notes")}: ${notes}` : "",
                                ],
                                t,
                        );

                        const successLine = t("ai-chat-tool-work-planner-create-task-success", {
                                title,
                                assignee: assignedName,
                                status: statusText,
                        });

                        return {
                                message: `${successLine}\n${response.message}`,
                                metadata: {
                                        ...response.metadata,
                                        toolId,
                                        taskId,
                                        payload,
                                        devices,
                                        status,
                                        type,
                                },
                        };
                }
                case TOOL_IDS.TREATMENT_HISTORY: {
                        const treatments = ((await getTreatments(userId)) || []).filter((treatment) => !treatment?.Archived);

                        treatments.sort((a, b) => getPrimaryTimestamp(b) - getPrimaryTimestamp(a));

                        const recentTreatments = treatments.slice(0, 5);

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-treatment-history-title"),
                                icon: "💊",
                                items: recentTreatments,
                                t,
                                formatItem: (treatment, index) => {
                                        const id = treatment?.ID || t("ai-chat-field-not-available");
                                        const medicine = treatment?.Medicine || t("ai-chat-field-not-available");
                                        const progress = `${Number(treatment?.TreatmentCount ?? 0)}/${Number(treatment?.TreatmentAmount ?? 0)}`;
                                        const nextDoseDate = calculateNextDoseDate(treatment);
                                        const nextDoseText = nextDoseDate
                                                ? formatDate(nextDoseDate, locale, t)
                                                : t("ai-chat-field-not-available");

                                        return `${index + 1}. ${t("ai-chat-field-animal-id")}: ${id} • ${t("ai-chat-field-medicine")}: ${medicine} • ${t("ai-chat-field-progress")}: ${progress} • ${t("ai-chat-field-next-dose")}: ${nextDoseText}`;
                                },
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                },
                        };
                }
                case TOOL_IDS.BIRTH_HISTORY: {
                        const lifecycleEvents = ((await getAllLifecycleEvents(userId)) || []).filter(
                                (event) => !event?.Archived
                        );

                        lifecycleEvents.sort((a, b) => getPrimaryTimestamp(b) - getPrimaryTimestamp(a));

                        const recentEvents = lifecycleEvents.slice(0, 10);

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-birth-history-title"),
                                icon: "🐄",
                                items: recentEvents,
                                t,
                                formatItem: (event, index) => {
                                        const id = event?.Uid || event?.ID || t("ai-chat-field-not-available");
                                        const dateText = formatDate(
                                                normalizeToDate(event?.t) ||
                                                        normalizeToDate(event?.transitionDate) ||
                                                        event?.StartDate,
                                                locale,
                                                t,
                                        );
                                        const stage = event?.stage || t("ai-chat-field-not-available");
                                        const previousStage = event?.previousStage || "-";
                                        const notes = (event?.notes || "").trim();
                                        const notesPart = notes ? ` • ${t("ai-chat-field-notes", { defaultValue: "Notes" })}: ${notes}` : "";

                                        return `${index + 1}. ${t("ai-chat-field-animal-id")}: ${id} • ${t("date")}: ${dateText} • ${t("ai-chat-field-stage", { defaultValue: "Stage" })}: ${previousStage} → ${stage}${notesPart}`;
                                },
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                },
                        };
                }
                case TOOL_IDS.EVENT_HISTORY: {
                        const events = ((await getAllGeneralEvents(userId)) || []).filter((event) => !event?.Archived);

                        events.sort((a, b) => getPrimaryTimestamp(b) - getPrimaryTimestamp(a));

                        const recentEvents = events.slice(0, 5);

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-event-history-title"),
                                icon: "📋",
                                items: recentEvents,
                                t,
                                formatItem: (event, index) => {
                                        const id = event?.ID || t("ai-chat-field-not-available");
                                        const dateText = formatDate(event?.t, locale, t);
                                        const diagnose = event?.Diagnose || t("ai-chat-field-not-available");
                                        const description = (event?.Description || "").trim();
                                        const descriptionPart = description ? ` • ${t("ai-chat-field-description")}: ${description}` : "";

                                        return `${index + 1}. ${t("ai-chat-field-animal-id")}: ${id} • ${t("ai-chat-field-event-type")}: ${diagnose} • ${t("date")}: ${dateText}${descriptionPart}`;
                                },
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                },
                        };
                }
                case TOOL_IDS.BILLING_STATUS: {
                        const billingState = (await getUserBillingState(userId)) || {};

                        const messageData = buildResponse(
                                `💳 ${t("ai-chat-tool-billing-status-title")}`,
                                [
                                        `${t("ai-chat-field-billing-plan")}: ${billingState.BillingState || t("ai-chat-field-not-available")}`,
                                        `${t("ai-chat-field-billing-price")}: ${
                                                billingState.BillingPrice !== undefined && billingState.BillingPrice !== null
                                                        ? billingState.BillingPrice
                                                        : t("ai-chat-field-not-available")
                                        }`,
                                        `${t("ai-chat-field-active-tags")}: ${
                                                billingState.ActiveTags !== undefined && billingState.ActiveTags !== null
                                                        ? billingState.ActiveTags
                                                        : t("ai-chat-field-not-available")
                                        }`,
                                        `${t("ai-chat-field-billing-start")}: ${formatDate(
                                                billingState.BillingDateStart,
                                                locale,
                                                t,
                                        )}`,
                                        `${t("ai-chat-field-billing-end")}: ${formatDate(
                                                billingState.BillingDateEnd,
                                                locale,
                                                t,
                                        )}`,
                                        `${t("ai-chat-field-billing-next")}: ${formatDate(
                                                billingState.BillingDate,
                                                locale,
                                                t,
                                        )}`,
                                ],
                                t,
                        );

                        return {
                                message: messageData.message,
                                metadata: {
                                        ...messageData.metadata,
                                        toolId,
                                },
                        };
                }
                case TOOL_IDS.NOTIFICATION_PREFERENCES: {
                        const notificationData = (await getUserNotificationData(userId)) || {};

                        const lines = Object.entries(notificationData).map(([key, value]) => {
                                const labelKey = `ai-chat-field-notification-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
                                const label = t(labelKey, {
                                        defaultValue: humanizeKey(key),
                                });

                                if (typeof value === "boolean") {
                                        return `${label}: ${formatBoolean(value, t)}`;
                                }

                                if (typeof value === "number") {
                                        return `${label}: ${value}`;
                                }

                                if (value === null || value === undefined || value === "") {
                                        return `${label}: ${t("ai-chat-field-not-available")}`;
                                }

                                return `${label}: ${value}`;
                        });

                        const messageData = buildResponse(
                                `🔔 ${t("ai-chat-tool-notification-preferences-title")}`,
                                lines,
                                t,
                        );

                        return {
                                message: messageData.message,
                                metadata: {
                                        ...messageData.metadata,
                                        toolId,
                                },
                        };
                }
                case TOOL_IDS.BIONODE_STATUS: {
                        const nodes = await getAllNodes(userId);

                        if (!nodes || nodes.length === 0) {
                                return {
                                        message: `📡 ${t("ai-chat-tool-bionode-status-title")}\n${t("ai-chat-tool-bionode-status-empty")}`,
                                        metadata: {
                                                toolId,
                                                empty: true,
                                                nodes: [],
                                        },
                                };
                        }

                        const summaryLines = formatNodeStatusSummary(nodes, t, locale);
                        const now = Date.now();

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-bionode-status-title"),
                                icon: "📡",
                                items: nodes,
                                t,
                                summaryLines,
                                formatItem: (node, index) => {
                                        const nodeId = node.ID || node.Id || t("ai-chat-field-not-available");
                                        const statusInfo = getNodeConnectivityStatus(node, now);
                                        const statusLabel = t(`ai-chat-field-node-status-${statusInfo.status}`, {
                                                defaultValue: statusInfo.status,
                                        });
                                        const lastUpdate = formatDate(node.t, locale, t);
                                        const group = (node.Group || "").trim();
                                        const groupPart = group ? ` • ${t("ai-chat-field-group")}: ${group}` : "";

                                        return `${index + 1}. ${t("ai-chat-field-node-id")}: ${nodeId} • ${t("ai-chat-field-status")}: ${statusLabel} • ${t("ai-chat-field-last-update")}: ${lastUpdate}${groupPart}`;
                                },
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                },
                        };
                }
                case TOOL_IDS.BIONODE_ANALYSIS: {
                        const nodeId = typeof payload.nodeId === "string" ? payload.nodeId.trim() : null;
                        const nodes = await getAllNodes(userId);

                        if (!nodes || nodes.length === 0) {
                                return {
                                        message: `🔍 ${t("ai-chat-tool-bionode-analysis-title")}\n${t("ai-chat-tool-bionode-analysis-empty")}`,
                                        metadata: {
                                                toolId,
                                                empty: true,
                                                nodes: [],
                                        },
                                };
                        }

                        let targetNode = null;
                        let analysisNodes = nodes;

                        if (nodeId) {
                                // Try to find in user's nodes first
                                targetNode = nodes.find(
                                        (n) =>
                                                (n.ID || n.Id || "").toLowerCase() === nodeId.toLowerCase() ||
                                                (n.UID || "").toLowerCase() === nodeId.toLowerCase()
                                );

                                // If not found, try global BioNodes collection
                                if (!targetNode) {
                                        const globalNode = await getBioNodeById(nodeId);
                                        if (globalNode) {
                                                targetNode = globalNode;
                                                analysisNodes = [globalNode];
                                        }
                                } else {
                                        analysisNodes = [targetNode];
                                }

                                if (!targetNode) {
                                        return {
                                                message: `🔍 ${t("ai-chat-tool-bionode-analysis-title")}\n${t("ai-chat-tool-bionode-analysis-not-found", { nodeId })}`,
                                                metadata: {
                                                        toolId,
                                                        nodeId,
                                                        empty: true,
                                                },
                                        };
                                }
                        }

                        const now = Date.now();
                        const troubleshootingGuidance = generateTroubleshootingGuidance(analysisNodes, t);

                        let analysisMessage = `🔍 ${t("ai-chat-tool-bionode-analysis-title")}\n\n`;

                        if (targetNode) {
                                const statusInfo = getNodeConnectivityStatus(targetNode, now);
                                const nodeId = targetNode.ID || targetNode.Id || t("ai-chat-field-not-available");
                                const lastUpdate = formatDate(targetNode.t, locale, t);
                                const group = (targetNode.Group || "").trim();
                                const type = (targetNode.Type || "").trim();

                                analysisMessage += `${t("ai-chat-field-node-id")}: ${nodeId}\n`;
                                analysisMessage += `${t("ai-chat-field-status")}: ${t(`ai-chat-field-node-status-${statusInfo.status}`, {
                                        defaultValue: statusInfo.status,
                                })}\n`;
                                analysisMessage += `${t("ai-chat-field-last-update")}: ${lastUpdate}\n`;

                                if (group) {
                                        analysisMessage += `${t("ai-chat-field-group")}: ${group}\n`;
                                }

                                if (type) {
                                        analysisMessage += `${t("ai-chat-field-type")}: ${type}\n`;
                                }

                                // Add statistics if available from global BioNodes
                                if (targetNode.statistics) {
                                        const stats = targetNode.statistics;
                                        if (stats.memory) {
                                                const memUsage = stats.memory.usage || 0;
                                                analysisMessage += `\n${t("ai-chat-field-memory-usage")}: ${memUsage.toFixed(1)}%\n`;
                                        }
                                        if (stats.cpu) {
                                                const loadAvg = stats.cpu.loadAvg?.[0] || 0;
                                                const cores = stats.cpu.cores || 1;
                                                const cpuPercent = ((loadAvg / cores) * 100).toFixed(1);
                                                analysisMessage += `${t("ai-chat-field-cpu-load")}: ${cpuPercent}%\n`;
                                        }
                                        if (stats.uptimeOS) {
                                                analysisMessage += `${t("ai-chat-field-system-uptime")}: ${formatDuration(stats.uptimeOS)}\n`;
                                        }
                                }
                        } else {
                                // Analysis for all nodes
                                const summaryLines = formatNodeStatusSummary(nodes, t, locale);
                                analysisMessage += summaryLines.join("\n") + "\n";
                        }

                        analysisMessage += `\n${t("ai-chat-tool-bionode-analysis-troubleshooting-title")}:\n`;
                        troubleshootingGuidance.forEach((rec, index) => {
                                analysisMessage += `${index + 1}. ${rec}\n`;
                        });

                        return {
                                message: analysisMessage,
                                metadata: {
                                        toolId,
                                        nodeId: targetNode ? (targetNode.ID || targetNode.Id) : null,
                                        nodes: analysisNodes,
                                        troubleshootingCount: troubleshootingGuidance.length,
                                },
                        };
                }
                case TOOL_IDS.ANIMAL_DETAILS: {
                        const animalIdOrMac = typeof payload.animalId === "string"
                                ? payload.animalId.trim()
                                : typeof payload.animalMac === "string"
                                        ? payload.animalMac.trim()
                                        : typeof payload.id === "string"
                                                ? payload.id.trim()
                                                : typeof payload.mac === "string"
                                                        ? payload.mac.trim()
                                                        : null;

                        if (!animalIdOrMac) {
                                return {
                                        message: `🐄 ${t("ai-chat-tool-animal-details-title")}\n${t("ai-chat-tool-animal-details-empty")}`,
                                        metadata: {
                                                toolId,
                                                empty: true,
                                        },
                                };
                        }

                        const animal = await findAnimalByIdOrMac(userId, animalIdOrMac);

                        if (!animal || !animal.MAC) {
                                return {
                                        message: `🐄 ${t("ai-chat-tool-animal-details-title")}\n${t("ai-chat-tool-animal-details-not-found", { animalId: animalIdOrMac })}`,
                                        metadata: {
                                                toolId,
                                                animalId: animalIdOrMac,
                                                empty: true,
                                        },
                                };
                        }

                        const mac = animal.MAC;
                        const animalId = animal.ID || animalIdOrMac;

                        // Fetch all data in parallel
                        const [treatments, births, lifecycleEvents, sensorData, generalEvents, predictions] = await Promise.all([
                                getTreatments(userId),
                                getBirths(userId, mac),
                                getLifecycleEvents(userId, mac),
                                getSensordata(userId, mac, "Day", Date.now() - 7 * 24 * 60 * 60 * 1000),
                                getGeneralEvents(userId, mac),
                                getAllPredictions(userId, false),
                        ]);

                        // Filter treatments by animal ID/MAC
                        const animalTreatments = ((treatments || []).filter((treatment) => {
                                if (treatment?.Archived) {
                                        return false;
                                }
                                const treatmentId = (treatment?.ID || "").toLowerCase();
                                const treatmentMac = (treatment?.MAC || treatment?.Mac || "").toLowerCase();
                                const normalizedAnimalId = animalId.toLowerCase();
                                const normalizedMac = mac.toLowerCase();
                                return treatmentId === normalizedAnimalId || treatmentMac === normalizedMac;
                        }) || []).sort((a, b) => getPrimaryTimestamp(b) - getPrimaryTimestamp(a));

                        // Filter predictions by MAC
                        const animalPredictions = (predictions || []).filter((prediction) => {
                                const predictionMac = (prediction?.MAC || "").toLowerCase();
                                return predictionMac === mac.toLowerCase();
                        });

                        // Build response message
                        let message = `🐄 ${t("ai-chat-tool-animal-details-title")}\n\n`;

                        // Animal Overview Section
                        message += `${t("ai-chat-field-animal-overview")}:\n`;
                        message += `${t("ai-chat-field-animal-id")}: ${animalId}\n`;
                        message += `${t("ai-chat-field-mac")}: ${mac}\n`;
                        if (animal.Group) {
                                message += `${t("ai-chat-field-group")}: ${animal.Group}\n`;
                        }
                        if (animal.Location && animal.Location !== "-") {
                                message += `${t("ai-chat-field-location")}: ${animal.Location}\n`;
                        }
                        if (animal.HealthScore !== undefined && animal.HealthScore !== null) {
                                message += `${t("ai-chat-field-health-score")}: ${animal.HealthScore.toFixed(1)}%\n`;
                        }
                        message += `${t("ai-chat-field-status")}: ${isAnimalActive(animal) ? t("ai-chat-field-active") : t("ai-chat-field-inactive")}\n`;

                        // Treatments Section
                        message += `\n${t("ai-chat-tool-treatment-history-title")}:\n`;
                        if (animalTreatments.length === 0) {
                                message += `${t("ai-chat-tool-empty-result")}\n`;
                        } else {
                                const recentTreatments = animalTreatments.slice(0, 5);
                                recentTreatments.forEach((treatment, index) => {
                                        const medicine = treatment?.Medicine || t("ai-chat-field-not-available");
                                        const progress = `${Number(treatment?.TreatmentCount ?? 0)}/${Number(treatment?.TreatmentAmount ?? 0)}`;
                                        const nextDoseDate = calculateNextDoseDate(treatment);
                                        const nextDoseText = nextDoseDate
                                                ? formatDate(nextDoseDate, locale, t)
                                                : t("ai-chat-field-not-available");
                                        message += `${index + 1}. ${t("ai-chat-field-medicine")}: ${medicine} • ${t("ai-chat-field-progress")}: ${progress} • ${t("ai-chat-field-next-dose")}: ${nextDoseText}\n`;
                                });
                        }

                        // Births Section
                        message += `\n${t("ai-chat-tool-birth-history-title")}:\n`;
                        if (!births || births.length === 0) {
                                message += `${t("ai-chat-tool-empty-result")}\n`;
                        } else {
                                const recentBirths = births.slice(0, 5).sort((a, b) => getPrimaryTimestamp(b) - getPrimaryTimestamp(a));
                                recentBirths.forEach((birth, index) => {
                                        const dateText = formatDate(
                                                normalizeToDate(birth?.t) ||
                                                        normalizeToDate(birth?.StartDateSeconds) ||
                                                        birth?.StartDate,
                                                locale,
                                                t,
                                        );
                                        const living = Number(birth?.Living ?? 0);
                                        const dead = Number(birth?.Dead ?? 0);
                                        const comment = (birth?.Comment || "").trim();
                                        const commentPart = comment ? ` • ${t("comment")}: ${comment}` : "";
                                        message += `${index + 1}. ${t("date")}: ${dateText} • ${t("ai-chat-field-living-offspring")}: ${living} • ${t("ai-chat-field-dead-offspring")}: ${dead}${commentPart}\n`;
                                });
                        }

                        // Lifecycle Events Section
                        message += `\n${t("ai-chat-tool-calf-lifecycle-overview-title", { defaultValue: "Lifecycle Events" })}:\n`;
                        if (!lifecycleEvents || lifecycleEvents.length === 0) {
                                message += `${t("ai-chat-tool-empty-result")}\n`;
                        } else {
                                const recentLifecycle = lifecycleEvents.slice(0, 5).sort((a, b) => getPrimaryTimestamp(b) - getPrimaryTimestamp(a));
                                recentLifecycle.forEach((event, index) => {
                                        const dateText = formatDate(
                                                normalizeToDate(event?.t) ||
                                                        normalizeToDate(event?.StartDate) ||
                                                        event?.StartDate,
                                                locale,
                                                t,
                                        );
                                        const stage = event?.stage || t("ai-chat-field-not-available");
                                        const notes = (event?.notes || "").trim();
                                        const notesPart = notes ? ` • ${t("ai-chat-field-notes")}: ${notes}` : "";
                                        message += `${index + 1}. ${t("date")}: ${dateText} • Stage: ${stage}${notesPart}\n`;
                                });
                        }

                        // Sensor Data Section
                        message += `\n${t("ai-chat-field-sensor-data")}:\n`;
                        if (!sensorData || sensorData.length === 0) {
                                message += `${t("ai-chat-tool-empty-result")}\n`;
                        } else {
                                const latestSensor = sensorData[sensorData.length - 1];
                                if (latestSensor) {
                                        const latestDate = formatDate(latestSensor.t, locale, t);
                                        const activity = Number(latestSensor.a || 0).toFixed(2);
                                        const temperature = Number(latestSensor.c || 0).toFixed(2);
                                        message += `${t("ai-chat-field-last-update")}: ${latestDate}\n`;
                                        message += `${t("ai-chat-field-activity")}: ${activity}\n`;
                                        message += `${t("ai-chat-field-temperature")}: ${temperature}°C\n`;
                                } else {
                                        message += `${t("ai-chat-tool-empty-result")}\n`;
                                }
                        }

                        // AI Predictions Section
                        message += `\n${t("ai-chat-field-ai-predictions")}:\n`;
                        if (!animalPredictions || animalPredictions.length === 0) {
                                message += `${t("ai-chat-tool-empty-result")}\n`;
                        } else {
                                const predictionTypes = ["Heat", "Birth", "Health", "Lameness"];
                                let hasPredictions = false;
                                predictionTypes.forEach((type) => {
                                        const typePredictions = animalPredictions.filter((p) => {
                                                // Check if prediction has type field or ID contains type
                                                const pType = p.type || (p.ID && p.ID.toLowerCase().includes(type.toLowerCase()) ? type : null);
                                                if (pType === type) {
                                                        return true;
                                                }
                                                // Also check if ID contains the type name
                                                if (p.ID && typeof p.ID === "string" && p.ID.toLowerCase().includes(type.toLowerCase())) {
                                                        return true;
                                                }
                                                return false;
                                        });
                                        if (typePredictions.length > 0) {
                                                const latestPrediction = typePredictions.sort((a, b) => {
                                                        const aTime = a.timestamp || a.t || 0;
                                                        const bTime = b.timestamp || b.t || 0;
                                                        return bTime - aTime;
                                                })[0];
                                                if (latestPrediction) {
                                                        const probability = latestPrediction.probability || latestPrediction.Probability || 0;
                                                        const predDate = formatDate(latestPrediction.timestamp || latestPrediction.t, locale, t);
                                                        message += `${type}: ${(Number(probability) * 100).toFixed(1)}% (${predDate})\n`;
                                                        hasPredictions = true;
                                                }
                                        }
                                });
                                if (!hasPredictions) {
                                        message += `${t("ai-chat-tool-empty-result")}\n`;
                                }
                        }

                        // General Events Section
                        message += `\n${t("ai-chat-tool-event-history-title")}:\n`;
                        if (!generalEvents || generalEvents.length === 0) {
                                message += `${t("ai-chat-tool-empty-result")}\n`;
                        } else {
                                const recentEvents = generalEvents.slice(0, 5).sort((a, b) => (b.t || 0) - (a.t || 0));
                                recentEvents.forEach((event, index) => {
                                        const dateText = formatDate(event.t, locale, t);
                                        const diagnose = event?.Diagnose || t("ai-chat-field-not-available");
                                        const description = (event?.Description || "").trim();
                                        const descriptionPart = description ? ` • ${t("ai-chat-field-description")}: ${description}` : "";
                                        message += `${index + 1}. ${t("ai-chat-field-event-type")}: ${diagnose} • ${t("date")}: ${dateText}${descriptionPart}\n`;
                                });
                        }

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        animalId,
                                        mac,
                                        treatmentsCount: animalTreatments.length,
                                        birthsCount: births?.length || 0,
                                        lifecycleEventsCount: lifecycleEvents?.length || 0,
                                        predictionsCount: animalPredictions.length,
                                        eventsCount: generalEvents?.length || 0,
                                        hasSensorData: sensorData && sensorData.length > 0,
                                },
                        };
                }
                case TOOL_IDS.CALF_LIFECYCLE_OVERVIEW: {
                        const lifecycleEvents = (await getAllLifecycleEvents(userId)) || [];
                        const animals = await getAnimals(userId);

                        // Count calves per current stage based on latest lifecycle event per animal
                        const latestStageByMac = new Map();
                        lifecycleEvents.forEach((event) => {
                                const mac = event?.MAC || event?.Uid;
                                if (!mac || !event?.stage) return;
                                const existing = latestStageByMac.get(mac);
                                if (!existing || (event.t || 0) > (existing.t || 0)) {
                                        latestStageByMac.set(mac, event);
                                }
                        });

                        const stageCounts = {};
                        latestStageByMac.forEach((event) => {
                                const stage = event.stage || "unknown";
                                stageCounts[stage] = (stageCounts[stage] || 0) + 1;
                        });

                        const stageLines = Object.entries(stageCounts)
                                .sort((a, b) => b[1] - a[1])
                                .map(([stage, count]) => `- ${stage}: ${count}`);

                        const title = `🐄 ${t("ai-chat-tool-calf-lifecycle-overview-title", { defaultValue: "Calf Lifecycle Overview" })}`;
                        const totalTracked = latestStageByMac.size;
                        const totalAnimals = animals.length;

                        const message = `${title}\nTotal animals: ${totalAnimals}\nWith lifecycle records: ${totalTracked}\n\n${stageLines.length > 0 ? stageLines.join("\n") : t("ai-chat-tool-empty-result")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        empty: stageLines.length === 0,
                                        stageCounts,
                                        totalTracked,
                                },
                        };
                }
                case TOOL_IDS.VACCINATION_STATUS: {
                        const animalIdOrMac = typeof payload.animalId === "string" ? payload.animalId.trim() : null;
                        const lookAheadDays = Math.min(Math.max(Number(payload.days) || 7, 1), 90);

                        const [allVaccinations, overdue, upcoming] = await Promise.all([
                                getAllVaccinations(userId),
                                getOverdueVaccinations(userId),
                                getUpcomingVaccinations(userId, lookAheadDays),
                        ]);

                        const title = `💉 ${t("ai-chat-tool-vaccination-status-title", { defaultValue: "Vaccination Status" })}`;
                        const lines = [];

                        lines.push(`Total vaccination records: ${allVaccinations.length}`);
                        lines.push(`Overdue: ${overdue.length}`);
                        lines.push(`Upcoming (next ${lookAheadDays} days): ${upcoming.length}`);

                        if (overdue.length > 0) {
                                lines.push(`\nOverdue vaccinations:`);
                                overdue.slice(0, 10).forEach((vax, i) => {
                                        const id = vax?.Uid || vax?.MAC || t("ai-chat-field-not-available");
                                        const name = vax?.vaccineName || vax?.name || "Unknown";
                                        const dueDate = formatDate(vax?.nextDueDate, locale, t);
                                        lines.push(`${i + 1}. ${id} • ${name} • Due: ${dueDate}`);
                                });
                        }

                        if (upcoming.length > 0) {
                                lines.push(`\nUpcoming vaccinations:`);
                                upcoming.slice(0, 10).forEach((vax, i) => {
                                        const id = vax?.Uid || vax?.MAC || t("ai-chat-field-not-available");
                                        const name = vax?.vaccineName || vax?.name || "Unknown";
                                        const dueDate = formatDate(vax?.nextDueDate, locale, t);
                                        lines.push(`${i + 1}. ${id} • ${name} • Due: ${dueDate}`);
                                });
                        }

                        return {
                                message: `${title}\n${lines.join("\n")}`,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: allVaccinations.length === 0,
                                        overdueCount: overdue.length,
                                        upcomingCount: upcoming.length,
                                        totalCount: allVaccinations.length,
                                },
                        };
                }
                case TOOL_IDS.WEIGHT_GROWTH_ANALYSIS: {
                        const animalIdOrMac = typeof payload.animalId === "string" ? payload.animalId.trim() : null;
                        const limit = Math.min(Math.max(Number(payload.limit) || 10, 1), 50);

                        const title = `⚖️ ${t("ai-chat-tool-weight-growth-analysis-title", { defaultValue: "Weight & Growth Analysis" })}`;
                        const lines = [];

                        if (animalIdOrMac) {
                                const animal = await findAnimalByIdOrMac(userId, animalIdOrMac);
                                if (!animal) {
                                        return {
                                                message: `${title}\n${t("ai-chat-tool-animal-details-not-found", { animalId: animalIdOrMac })}`,
                                                metadata: { toolId, payload, empty: true },
                                        };
                                }
                                const records = await getWeightRecordsByAnimal(userId, animal.MAC);
                                const adgResult = calculateADG(records, animal.BirthWeight, animal.BirthDate);
                                const anomalies = detectWeightAnomalies(records);

                                lines.push(`Animal: ${animal.ID || animal.MAC}`);
                                if (adgResult.currentWeight !== null) lines.push(`Current weight: ${adgResult.currentWeight.toFixed(1)} kg`);
                                if (adgResult.adg !== null) lines.push(`ADG: ${adgResult.adg.toFixed(3)} kg/day`);
                                if (adgResult.gainTotal !== null) lines.push(`Total gain: ${adgResult.gainTotal.toFixed(1)} kg`);
                                lines.push(`Days tracked: ${adgResult.daysTracked}`);
                                lines.push(`Weight records: ${records.length}`);

                                if (anomalies.length > 0) {
                                        lines.push(`\nAnomalies detected (${anomalies.length}):`);
                                        anomalies.slice(0, 5).forEach((a) => lines.push(`- ${a.message}`));
                                }

                                if (records.length > 0) {
                                        lines.push(`\nRecent records:`);
                                        records.slice(-limit).reverse().forEach((rec, i) => {
                                                const dateText = formatDate(rec.t, locale, t);
                                                lines.push(`${i + 1}. ${dateText}: ${rec.weight?.toFixed(1) || "?"} kg`);
                                        });
                                }
                        } else {
                                const allRecords = await getAllWeightRecords(userId);
                                const animals = await getAnimals(userId);

                                const macSet = new Set(allRecords.map((r) => r.MAC).filter(Boolean));
                                lines.push(`Total weight records: ${allRecords.length}`);
                                lines.push(`Animals with weight data: ${macSet.size}`);
                                lines.push(`Total herd size: ${animals.length}`);

                                if (allRecords.length > 0) {
                                        const weights = allRecords.map((r) => r.weight).filter((w) => typeof w === "number");
                                        if (weights.length > 0) {
                                                const avg = weights.reduce((a, b) => a + b, 0) / weights.length;
                                                const min = Math.min(...weights);
                                                const max = Math.max(...weights);
                                                lines.push(`\nWeight summary:`);
                                                lines.push(`- Average: ${avg.toFixed(1)} kg`);
                                                lines.push(`- Min: ${min.toFixed(1)} kg`);
                                                lines.push(`- Max: ${max.toFixed(1)} kg`);
                                        }
                                }
                        }

                        return {
                                message: `${title}\n${lines.join("\n")}`,
                                metadata: { toolId, payload, empty: lines.length === 0 },
                        };
                }
                case TOOL_IDS.DISEASE_OVERVIEW: {
                        const animalIdOrMac = typeof payload.animalId === "string" ? payload.animalId.trim() : null;
                        const category = typeof payload.category === "string" ? payload.category.trim() : null;

                        const title = `🦠 ${t("ai-chat-tool-disease-overview-title", { defaultValue: "Disease Overview" })}`;
                        const lines = [];

                        let records;
                        if (animalIdOrMac) {
                                const animal = await findAnimalByIdOrMac(userId, animalIdOrMac);
                                if (!animal) {
                                        return {
                                                message: `${title}\n${t("ai-chat-tool-animal-details-not-found", { animalId: animalIdOrMac })}`,
                                                metadata: { toolId, payload, empty: true },
                                        };
                                }
                                records = await getDiseaseRecordsByAnimal(userId, animal.MAC);
                                lines.push(`Animal: ${animal.ID || animal.MAC}`);
                        } else {
                                records = await getAllDiseaseRecords(userId);
                        }

                        if (category) {
                                records = records.filter((r) => r.category === category);
                                lines.push(`Category filter: ${category}`);
                        }

                        lines.push(`Total disease records: ${records.length}`);

                        const activeRecords = records.filter((r) => !r.outcome || r.outcome === "ongoing");
                        lines.push(`Active/ongoing cases: ${activeRecords.length}`);

                        // Category breakdown
                        const categoryCounts = {};
                        records.forEach((r) => {
                                const cat = r.category || "other";
                                categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                        });
                        if (Object.keys(categoryCounts).length > 0) {
                                lines.push(`\nBy category:`);
                                Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).forEach(([cat, count]) => {
                                        lines.push(`- ${cat}: ${count}`);
                                });
                        }

                        // Wisconsin scores
                        const withWisconsin = records.filter((r) => r.wisconsinScores);
                        if (withWisconsin.length > 0) {
                                const totalScores = withWisconsin.map((r) => {
                                        const result = classifyWisconsinScore(r.wisconsinScores);
                                        return result.total;
                                });
                                const avgScore = totalScores.reduce((a, b) => a + b, 0) / totalScores.length;
                                lines.push(`\nWisconsin scores: ${withWisconsin.length} records`);
                                lines.push(`Average total score: ${avgScore.toFixed(1)}`);
                        }

                        // Recent records
                        if (records.length > 0) {
                                lines.push(`\nRecent records:`);
                                records.slice(0, 5).forEach((rec, i) => {
                                        const id = rec.Uid || rec.MAC || "?";
                                        const dateText = formatDate(rec.t, locale, t);
                                        const cat = rec.category || "other";
                                        const outcome = rec.outcome || "ongoing";
                                        lines.push(`${i + 1}. ${id} • ${cat} • ${outcome} • ${dateText}`);
                                });
                        }

                        return {
                                message: `${title}\n${lines.join("\n")}`,
                                metadata: { toolId, payload, empty: records.length === 0, totalRecords: records.length, activeCount: activeRecords.length },
                        };
                }
                case TOOL_IDS.CALF_HEALTH_KPIS: {
                        const [animals, weightRecords, diseaseRecords, vaccinations] = await Promise.all([
                                getAnimals(userId),
                                getAllWeightRecords(userId),
                                getAllDiseaseRecords(userId),
                                getAllVaccinations(userId),
                        ]);

                        const kpis = calculateCalfKPIs({
                                animals,
                                weightRecords,
                                diseaseRecords,
                                vaccinations,
                        });

                        const title = `📊 ${t("ai-chat-tool-calf-health-kpis-title", { defaultValue: "Calf Health KPIs" })}`;
                        const lines = [];

                        const kpiEntries = [
                                { label: "Mortality Rate", data: kpis.mortalityRate, unit: "%" },
                                { label: "Average ADG", data: kpis.averageADG, unit: " kg/day" },
                                { label: "Vaccination Compliance", data: kpis.vaccinationCompliance, unit: "%" },
                                { label: "Disease Incidence", data: kpis.diseaseIncidence, unit: "%" },
                                { label: "Scours Rate", data: kpis.scoursRate, unit: "%" },
                                { label: "BRD Rate", data: kpis.brdRate, unit: "%" },
                                { label: "Avg Wisconsin Score", data: kpis.averageWisconsinScore, unit: "" },
                        ];

                        kpiEntries.forEach(({ label, data, unit }) => {
                                if (data && data.value !== undefined && data.value !== null) {
                                        const statusIcon = data.status === "good" ? "✅" : data.status === "warning" ? "⚠️" : data.status === "critical" ? "🔴" : "ℹ️";
                                        lines.push(`${statusIcon} ${label}: ${typeof data.value === "number" ? data.value.toFixed(1) : data.value}${unit}`);
                                }
                        });

                        lines.push(`\nTotal animals: ${animals.length}`);

                        return {
                                message: `${title}\n${lines.join("\n")}`,
                                metadata: { toolId, empty: false, kpis },
                        };
                }
                case TOOL_IDS.FARM_KPI_OVERVIEW: {
                        const limit = normalizeLimit(payload.limit ?? payload.max ?? params.limit, {
                                min: 1,
                                max: 10,
                                fallback: 5,
                        });

                        const queryOptions = {};
                        const dateCandidate = payload.date ?? params.date;
                        const startCandidate = payload.startDate ?? payload.start ?? params.startDate ?? params.start;
                        const endCandidate = payload.endDate ?? payload.end ?? params.endDate ?? params.end;
                        const periodTypeCandidate = payload.periodType ?? payload.period ?? params.periodType;

                        if (typeof dateCandidate === "string" && dateCandidate.trim()) {
                                queryOptions.date = dateCandidate.trim();
                        }

                        if (typeof startCandidate === "string" && startCandidate.trim()) {
                                queryOptions.startDate = startCandidate.trim();
                        }

                        if (typeof endCandidate === "string" && endCandidate.trim()) {
                                queryOptions.endDate = endCandidate.trim();
                        }

                        if (typeof periodTypeCandidate === "string" && periodTypeCandidate.trim()) {
                                queryOptions.periodType = periodTypeCandidate.trim();
                        }

                        let metrics = [];
                        try {
                                const rawMetrics = await getKPIMetrics(userId, queryOptions);
                                if (Array.isArray(rawMetrics)) {
                                        metrics = [...rawMetrics]
                                                .filter((entry) => entry && typeof entry === "object")
                                                .sort((a, b) => {
                                                        const dateA = typeof a.date === "string" ? a.date : "";
                                                        const dateB = typeof b.date === "string" ? b.date : "";
                                                        return dateB.localeCompare(dateA);
                                                });
                                }
                        } catch (error) {
                                console.warn("[chatToolsService][FARM_KPI_OVERVIEW] - Failed to load KPI metrics:", error);
                        }

                        const limitedMetrics = metrics.slice(0, limit);
                        const summaryLine = summarizeKpiMetric(limitedMetrics[0], locale, t);

                        const listMessage = buildListMessage({
                                title: t("ai-chat-tool-farm-kpi-overview-title"),
                                icon: "📊",
                                items: limitedMetrics,
                                t,
                                summaryLines: summaryLine ? [summaryLine] : [],
                                formatItem: (metric, index) => formatKpiListItem(metric, index, locale, t),
                        });

                        return {
                                message: listMessage.message,
                                metadata: {
                                        ...listMessage.metadata,
                                        toolId,
                                        payload,
                                        query: queryOptions,
                                        latest: limitedMetrics[0] || null,
                                },
                        };
                }
                case TOOL_IDS.FARM_KPI_TRENDS: {
                        const kpiName = [payload.kpi, payload.kpiName, params.kpi]
                                .map((value) => (typeof value === "string" ? value.trim() : ""))
                                .find((value) => value);

                        const title = `📈 ${t("ai-chat-tool-farm-kpi-trends-title")}`;

                        if (!kpiName) {
                                return {
                                        message: `${title}\n${t("ai-chat-tool-farm-kpi-trends-missing-kpi")}`,
                                        metadata: {
                                                toolId,
                                                payload,
                                                empty: true,
                                        },
                                };
                        }

                        const limit = normalizeLimit(payload.limit ?? payload.points ?? params.limit, {
                                min: 2,
                                max: 24,
                                fallback: 6,
                        });

                        const trendQuery = {};
                        const startCandidate = payload.startDate ?? payload.start ?? params.startDate ?? params.start;
                        const endCandidate = payload.endDate ?? payload.end ?? params.endDate ?? params.end;
                        const periodTypeCandidate = payload.periodType ?? payload.period ?? params.periodType;

                        if (typeof startCandidate === "string" && startCandidate.trim()) {
                                trendQuery.startDate = startCandidate.trim();
                        }

                        if (typeof endCandidate === "string" && endCandidate.trim()) {
                                trendQuery.endDate = endCandidate.trim();
                        }

                        if (typeof periodTypeCandidate === "string" && periodTypeCandidate.trim()) {
                                trendQuery.periodType = periodTypeCandidate.trim();
                        }

                        let trendData = [];
                        try {
                                const rawTrend = await getKPITrends(userId, kpiName, trendQuery);
                                if (Array.isArray(rawTrend)) {
                                        trendData = rawTrend.filter((entry) => entry && typeof entry === "object");
                                }
                        } catch (error) {
                                console.warn("[chatToolsService][FARM_KPI_TRENDS] - Failed to load KPI trends:", error);
                        }

                        const trimmedTrend = trendData.slice(-limit);

                        if (trimmedTrend.length === 0) {
                                return {
                                        message: `${title}\n${t("ai-chat-tool-empty-result")}`,
                                        metadata: {
                                                toolId,
                                                payload,
                                                kpi: kpiName,
                                                query: trendQuery,
                                                empty: true,
                                        },
                                };
                        }

                        const firstPoint = trimmedTrend[0];
                        const lastPoint = trimmedTrend[trimmedTrend.length - 1];
                        const primaryKpi = resolvePrimaryKpi(lastPoint) || kpiName;
                        const changeText = formatKpiChange(firstPoint, lastPoint, primaryKpi, locale, t);
                        const kpiLabel = resolveKpiLabel(kpiName, t);

                        const lines = trimmedTrend.map((point) => {
                                const dateLabel = formatDateLabel(point.date, locale, t);
                                const value = point.value !== undefined ? point.value : point[primaryKpi];
                                const formattedValue = formatKpiValue(primaryKpi, value, locale, t);
                                return `${dateLabel}: ${formattedValue}`;
                        });

                        const headerLines = [
                                `${t("ai-chat-field-kpi")}: ${kpiLabel}`,
                                trendQuery.periodType
                                        ? `${t("ai-chat-field-period-type")}: ${formatPeriodType(trendQuery.periodType, t)}`
                                        : null,
                                trendQuery.startDate || trendQuery.endDate
                                        ? `${t("ai-chat-field-date-range")}: ${[
                                                      trendQuery.startDate
                                                              ? formatDateLabel(trendQuery.startDate, locale, t)
                                                              : t("ai-chat-field-date-not-set"),
                                                      trendQuery.endDate
                                                              ? formatDateLabel(trendQuery.endDate, locale, t)
                                                              : t("ai-chat-field-date-not-set"),
                                              ].join(" → ")}`
                                        : null,
                                changeText,
                        ].filter(Boolean);

                        const message = `${title}\n${headerLines.join("\n")}\n\n${lines.join("\n")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        kpi: kpiName,
                                        query: trendQuery,
                                        points: trimmedTrend,
                                        change: changeText,
                                },
                        };
                }
                case TOOL_IDS.SEARCH_KNOWLEDGE_BASE: {
                        const query = payload.query || "";
                        if (!query.trim()) {
                                return {
                                        message: t("ai-chat-tool-search-kb-missing-query", { defaultValue: "Please provide a search query." }),
                                        metadata: { toolId, payload, empty: true },
                                };
                        }

                        const sections = await retrieveRelevantKnowledge(query, { maxResults: 2 });
                        let content = "";
                        
                        if (sections.length > 0) {
                                const contents = sections.map(sectionKey => {
                                        const section = getKnowledgeSection(sectionKey, locale);
                                        return section ? `## ${section.title}\n${section.content}` : null;
                                }).filter(Boolean);
                                content = contents.join("\n\n");
                        }

                        if (!content) {
                                content = t("ai-chat-tool-search-kb-no-results", { defaultValue: "No relevant knowledge base articles found." });
                        }

                        return {
                                message: content,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: !content,
                                        sections,
                                },
                        };
                }
                case TOOL_IDS.PRODUCTION_HISTORY: {
                        const recordType = payload.recordType;
                        const identifier = payload.animalId || payload.mac || payload.id;
                        const limit = Math.min(Math.max(Number(payload.limit) || 20, 1), 50);

                        if (!identifier) {
                                return {
                                        message: t("ai-chat-tool-production-history-missing-id", { defaultValue: "Animal ID is required." }),
                                        metadata: { toolId, payload, empty: true },
                                };
                        }

                        if (!recordType) {
                                return {
                                        message: t("ai-chat-tool-production-history-missing-type", { defaultValue: "Record type is required." }),
                                        metadata: { toolId, payload, empty: true },
                                };
                        }

                        let mac = identifier;
                        let animalId = identifier;

                        // Try to resolve animal ID if it looks like an ID (not a MAC)
                        if (!identifier.includes(":")) {
                                const animal = await findAnimalByIdOrMac(userId, identifier);
                                if (animal && animal.MAC) {
                                        mac = animal.MAC;
                                        animalId = animal.ID || identifier;
                                } else {
                                        return {
                                                message: t("ai-chat-tool-production-history-not-found", { defaultValue: "Animal not found.", animalId: identifier }),
                                                metadata: { toolId, payload, empty: true, animalId: identifier },
                                        };
                                }
                        }

                        const records = await getProductionRecords(userId, recordType, mac, limit);

                        if (!records || records.length === 0) {
                                return {
                                        message: t("ai-chat-tool-production-history-empty", { defaultValue: "No production records found for this animal.", animalId, recordType }),
                                        metadata: { toolId, payload, empty: true, animalId, recordType },
                                };
                        }

                        // Format the records into a readable string
                        const formattedRecords = records.map(record => {
                                const date = formatDateLabel(record.t || record.date || record.Date, locale, t);
                                // Filter out internal fields
                                const displayFields = Object.entries(record)
                                        .filter(([key]) => !["t", "id", "ID", "MAC", "Uid", "Archived"].includes(key))
                                        .map(([key, value]) => `${key}: ${value}`)
                                        .join(", ");
                                return `- ${date}: ${displayFields}`;
                        }).join("\n");

                        const title = `📋 ${t("ai-chat-tool-production-history-title", { defaultValue: "Production History" })}`;
                        const message = `${title} (${recordType})\nAnimal: ${animalId}\n\n${formattedRecords}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: false,
                                        animalId,
                                        recordType,
                                        count: records.length,
                                },
                        };
                }

                // ==================== NEW DIAGNOSTIC AND ANALYSIS TOOLS ====================

                case TOOL_IDS.HEALTH_DIAGNOSIS_ASSIST: {
                        const animalId = payload.animalId || payload.mac || payload.id;
                        const symptoms = Array.isArray(payload.symptoms) ? payload.symptoms : [];
                        const includeHistory = payload.includeHistory !== false;

                        if (!animalId) {
                                return {
                                        message: t("ai-chat-tool-animal-details-empty", { defaultValue: "Please specify an animal ID or MAC address." }),
                                        metadata: { toolId, payload, empty: true },
                                };
                        }

                        const animal = await findAnimalByIdOrMac(userId, animalId);
                        if (!animal) {
                                return {
                                        message: t("ai-chat-tool-animal-details-not-found", { animalId, defaultValue: `Animal not found: ${animalId}` }),
                                        metadata: { toolId, payload, empty: true, animalId },
                                };
                        }

                        // Gather comprehensive data for diagnosis
                        const [treatments, , predictions] = await Promise.all([
                                includeHistory ? getTreatments(userId) : Promise.resolve([]),
                                includeHistory ? getGeneralEvents(userId, animal.MAC) : Promise.resolve([]),
                                getAllPredictions(userId, true),
                                getAnimals(userId),
                        ]);

                        // Filter to this animal's treatments
                        const animalTreatments = treatments.filter(t =>
                                t.AnimalId === animal.ID || t.MAC === animal.MAC || t.AnimalMac === animal.MAC
                        ).slice(0, 10);

                        // Get predictions for this animal
                        const animalPredictions = predictions.filter(p => p.MAC === animal.MAC);

                        // Calculate health assessment
                        const healthScore = typeof animal.HealthScore === "number" ? animal.HealthScore : null;
                        // Derive urgency from the canonical health state rather than from raw
                        // score cut-offs: the previous score ladder compared against
                        // SUSPICIOUS_HEALTH_SCORE_THRESHOLD (30) right after a `< 30` test,
                        // which made the "high" tier unreachable and ignored HealthStatus/
                        // HealthSeverity/DataStale entirely.
                        const healthState = getAnimalHealthState(animal);
                        const URGENCY_BY_HEALTH_STATE = {
                                emergency: "critical",
                                critical: "critical",
                                warning: "high",
                                attention: "medium",
                                healthy: "low",
                        };
                        const urgencyLevel = URGENCY_BY_HEALTH_STATE[healthState] || "unknown";

                        // Build diagnosis analysis
                        const analysisLines = [];

                        // Current status
                        analysisLines.push(`**${t("ai-chat-field-animal-id", { defaultValue: "Animal" })}:** ${animal.ID || animal.MAC}`);
                        if (healthScore !== null) {
                                analysisLines.push(`**${t("ai-chat-field-health-score", { defaultValue: "Health Score" })}:** ${healthScore.toFixed(1)}% (${urgencyLevel} priority)`);
                        }
                        if (animal.Group) {
                                analysisLines.push(`**${t("ai-chat-field-group", { defaultValue: "Group" })}:** ${animal.Group}`);
                        }

                        // Reported symptoms
                        if (symptoms.length > 0) {
                                analysisLines.push(`\n**Reported Symptoms:** ${symptoms.join(", ")}`);
                        }

                        // Health interpretation, keyed off the same canonical state as urgencyLevel.
                        if (urgencyLevel !== "unknown") {
                                analysisLines.push("\n**Health Score Analysis:**");
                                if (urgencyLevel === "critical") {
                                        analysisLines.push("- CRITICAL: Immediate veterinary attention recommended");
                                        analysisLines.push("- Health score indicates severe health compromise");
                                } else if (urgencyLevel === "high") {
                                        analysisLines.push("- HIGH PRIORITY: Animal requires prompt investigation");
                                        analysisLines.push("- Suspicious status triggered - monitor closely");
                                } else if (urgencyLevel === "medium") {
                                        analysisLines.push("- MODERATE: Some deviation from normal patterns detected");
                                        analysisLines.push("- Consider increased monitoring");
                                } else {
                                        analysisLines.push("- Health score within normal range");
                                }
                        } else if (healthState === "stale") {
                                analysisLines.push("\n**Health Score Analysis:**");
                                analysisLines.push("- NO CURRENT DATA: Sensor readings are stale, health state cannot be assessed");
                        }

                        // Treatment history summary
                        if (animalTreatments.length > 0) {
                                analysisLines.push(`\n**Recent Treatments (${animalTreatments.length}):**`);
                                animalTreatments.slice(0, 5).forEach(treatment => {
                                        const date = formatDateLabel(treatment.StartDate || treatment.t, locale, t);
                                        const name = treatment.TreatmentName || treatment.Name || "Treatment";
                                        analysisLines.push(`- ${date}: ${name}`);
                                });
                        } else if (includeHistory) {
                                analysisLines.push("\n**Treatment History:** No recent treatments recorded");
                        }

                        // Predictions
                        if (animalPredictions.length > 0) {
                                analysisLines.push("\n**Active Predictions:**");
                                animalPredictions.forEach(pred => {
                                        const type = pred.Type || pred.type || "Prediction";
                                        const confidence = pred.Confidence || pred.confidence;
                                        analysisLines.push(`- ${type}${confidence ? ` (${(confidence * 100).toFixed(0)}% confidence)` : ""}`);
                                });
                        }

                        // Possible conditions based on symptoms and health score
                        const possibleConditions = [];
                        if (symptoms.some(s => /cough|breath|respir/i.test(s))) {
                                possibleConditions.push("Respiratory infection (BRD, pneumonia, IBR)");
                        }
                        if (symptoms.some(s => /limp|lame|walk|leg/i.test(s))) {
                                possibleConditions.push("Lameness (foot rot, joint infection, injury)");
                        }
                        if (symptoms.some(s => /scour|diarr|loose/i.test(s))) {
                                possibleConditions.push("Scours/Diarrhea (cryptosporidium, rotavirus, E.coli, coccidia)");
                        }
                        if (symptoms.some(s => /eat|appetite|feed/i.test(s))) {
                                possibleConditions.push("Gastrointestinal issue or systemic illness");
                        }
                        if (symptoms.some(s => /fever|hot|temperature/i.test(s))) {
                                possibleConditions.push("Infection or inflammatory condition");
                        }
                        if (possibleConditions.length === 0 && requiresAnimalHealthReview(animal)) {
                                possibleConditions.push("Health anomaly detected - recommend physical examination");
                        }

                        if (possibleConditions.length > 0) {
                                analysisLines.push("\n**Possible Conditions to Investigate:**");
                                possibleConditions.forEach(condition => {
                                        analysisLines.push(`- ${condition}`);
                                });
                        }

                        // Recommendations
                        analysisLines.push("\n**Recommended Actions:**");
                        if (urgencyLevel === "critical") {
                                analysisLines.push("1. Contact veterinarian immediately");
                                analysisLines.push("2. Isolate animal from group");
                                analysisLines.push("3. Document all observed symptoms");
                        } else if (urgencyLevel === "high") {
                                analysisLines.push("1. Physical examination within 24 hours");
                                analysisLines.push("2. Check temperature and visible symptoms");
                                analysisLines.push("3. Review recent feed/water intake if available");
                        } else {
                                analysisLines.push("1. Continue monitoring health score trends");
                                analysisLines.push("2. Note any behavioral changes");
                        }

                        const title = `🩺 ${t("ai-chat-tool-health-diagnosis-assist-title", { defaultValue: "Health Diagnosis Assist" })}`;
                        const message = `${title}\n\n${analysisLines.join("\n")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: false,
                                        animalId: animal.ID || animal.MAC,
                                        mac: animal.MAC,
                                        healthScore,
                                        urgency: urgencyLevel,
                                        symptoms,
                                        possibleConditions,
                                        treatmentCount: animalTreatments.length,
                                },
                        };
                }

                case TOOL_IDS.COMPARATIVE_ANALYSIS: {
                        const compareType = payload.compareType || "groups";
                        const subjects = Array.isArray(payload.subjects) ? payload.subjects : [];

                        if (subjects.length < 2) {
                                return {
                                        message: t("ai-chat-tool-comparative-analysis-need-subjects", { defaultValue: "Please specify at least 2 items to compare." }),
                                        metadata: { toolId, payload, empty: true },
                                };
                        }

                        const animals = await getAnimals(userId);
                        const treatments = await getTreatments(userId);
                        await getAllPredictions(userId, true);

                        const analysisLines = [];
                        const title = `📊 ${t("ai-chat-tool-comparative-analysis-title", { defaultValue: "Comparative Analysis" })}`;

                        if (compareType === "groups") {
                                // Compare animal groups
                                const groupData = {};

                                subjects.forEach(groupName => {
                                        const groupAnimals = animals.filter(a =>
                                                a.Group && a.Group.toLowerCase() === groupName.toLowerCase()
                                        );

                                        if (groupAnimals.length > 0) {
                                                const healthScores = groupAnimals
                                                        .map(a => a.HealthScore)
                                                        .filter(s => typeof s === "number");

                                                const avgHealth = healthScores.length > 0
                                                        ? healthScores.reduce((a, b) => a + b, 0) / healthScores.length
                                                        : null;

                                                const suspiciousCount = groupAnimals.filter(
                                                        (a) =>
                                                                typeof a.HealthScore === "number" &&
                                                                a.HealthScore < SUSPICIOUS_HEALTH_SCORE_THRESHOLD
                                                ).length;

                                                const groupTreatments = treatments.filter(t => {
                                                        const animal = groupAnimals.find(a =>
                                                                a.ID === t.AnimalId || a.MAC === t.MAC || a.MAC === t.AnimalMac
                                                        );
                                                        return !!animal;
                                                });

                                                groupData[groupName] = {
                                                        count: groupAnimals.length,
                                                        avgHealthScore: avgHealth,
                                                        suspiciousCount,
                                                        treatmentCount: groupTreatments.length,
                                                };
                                        }
                                });

                                analysisLines.push(`**Comparison Type:** Groups\n`);

                                Object.entries(groupData).forEach(([groupName, data]) => {
                                        analysisLines.push(`**${groupName}:**`);
                                        analysisLines.push(`- Animals: ${data.count}`);
                                        if (data.avgHealthScore !== null) {
                                                analysisLines.push(`- Avg Health Score: ${data.avgHealthScore.toFixed(1)}%`);
                                        }
                                        analysisLines.push(`- Suspicious: ${data.suspiciousCount}`);
                                        analysisLines.push(`- Active Treatments: ${data.treatmentCount}`);
                                        analysisLines.push("");
                                });

                                // Summary comparison
                                if (Object.keys(groupData).length >= 2) {
                                        const entries = Object.entries(groupData);
                                        const healthDiffs = entries.map(([name, data]) => ({ name, health: data.avgHealthScore || 0 }));
                                        healthDiffs.sort((a, b) => b.health - a.health);

                                        analysisLines.push("**Summary:**");
                                        if (healthDiffs[0].health - healthDiffs[healthDiffs.length - 1].health > 10) {
                                                analysisLines.push(`- ${healthDiffs[0].name} has notably better health scores than ${healthDiffs[healthDiffs.length - 1].name}`);
                                        }
                                }

                        } else if (compareType === "animals") {
                                // Compare individual animals
                                analysisLines.push(`**Comparison Type:** Individual Animals\n`);

                                for (const subjectId of subjects.slice(0, 5)) {
                                        const animal = await findAnimalByIdOrMac(userId, subjectId);
                                        if (animal) {
                                                const animalTreatments = treatments.filter(t =>
                                                        t.AnimalId === animal.ID || t.MAC === animal.MAC
                                                );

                                                analysisLines.push(`**${animal.ID || animal.MAC}:**`);
                                                if (typeof animal.HealthScore === "number") {
                                                        analysisLines.push(`- Health Score: ${animal.HealthScore.toFixed(1)}%`);
                                                }
                                                if (animal.Group) {
                                                        analysisLines.push(`- Group: ${animal.Group}`);
                                                }
                                                analysisLines.push(`- Treatments: ${animalTreatments.length}`);
                                                analysisLines.push("");
                                        }
                                }

                        } else if (compareType === "periods") {
                                // Compare time periods
                                analysisLines.push(`**Comparison Type:** Time Periods\n`);
                                analysisLines.push("Note: Use farm-kpi-trends tool for detailed period comparison.");
                                analysisLines.push(`Requested periods: ${subjects.join(", ")}`);
                        }

                        const message = `${title}\n\n${analysisLines.join("\n")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: false,
                                        compareType,
                                        subjects,
                                },
                        };
                }

                case TOOL_IDS.GENERATE_REPORT: {
                        const reportType = payload.reportType || "weekly-summary";
                        const focusGroup = payload.focusGroup || null;

                        // Calculate date range
                        const now = new Date();
                        const endDate = payload.endDate ? new Date(payload.endDate) : now;
                        const defaultStartDays = reportType === "weekly-summary" ? 7 : 30;
                        const startDate = payload.startDate
                                ? new Date(payload.startDate)
                                : new Date(endDate.getTime() - defaultStartDays * 24 * 60 * 60 * 1000);

                        // Gather data
                        const [animals, treatments, predictions, vaccinations, diseaseRecords] = await Promise.all([
                                getAnimals(userId),
                                getTreatments(userId),
                                getAllPredictions(userId, true),
                                getAllVaccinations(userId),
                                getAllDiseaseRecords(userId),
                        ]);

                        // Filter by focus group if specified
                        const filteredAnimals = focusGroup
                                ? animals.filter(a => a.Group && a.Group.toLowerCase() === focusGroup.toLowerCase())
                                : animals;

                        const reportLines = [];
                        const title = `📋 ${getReportTitle(reportType, t)}`;

                        reportLines.push(`**Period:** ${formatDateLabel(startDate, locale, t)} - ${formatDateLabel(endDate, locale, t)}`);
                        if (focusGroup) {
                                reportLines.push(`**Focus Group:** ${focusGroup}`);
                        }
                        reportLines.push("");

                        // Health scores
                        const healthScores = filteredAnimals
                                .map(a => a.HealthScore)
                                .filter(s => typeof s === "number");
                        const avgHealth = healthScores.length > 0
                                ? healthScores.reduce((a, b) => a + b, 0) / healthScores.length
                                : null;
                        const suspiciousCount = filteredAnimals.filter(
                                (a) =>
                                        typeof a.HealthScore === "number" &&
                                        a.HealthScore < SUSPICIOUS_HEALTH_SCORE_THRESHOLD
                        ).length;

                        if (reportType === "weekly-summary" || reportType === "health-overview") {
                                reportLines.push("**Health Summary:**");
                                reportLines.push(`- Total Animals: ${filteredAnimals.length}`);
                                if (avgHealth !== null) {
                                        reportLines.push(`- Average Health Score: ${avgHealth.toFixed(1)}%`);
                                }
                                reportLines.push(`- Suspicious Animals: ${suspiciousCount}`);
                                reportLines.push("");
                        }

                        if (reportType === "weekly-summary" || reportType === "treatment-summary") {
                                // Filter treatments by date
                                const periodTreatments = treatments.filter(t => {
                                        const treatmentDate = t.StartDate ? new Date(t.StartDate) : (t.t ? new Date(t.t) : null);
                                        return treatmentDate && treatmentDate >= startDate && treatmentDate <= endDate;
                                });

                                reportLines.push("**Treatment Summary:**");
                                reportLines.push(`- Treatments in Period: ${periodTreatments.length}`);

                                const activeTreatments = treatments.filter(t => t.Status === "active" || !t.EndDate);
                                reportLines.push(`- Currently Active: ${activeTreatments.length}`);

                                const overdueTreatments = treatments.filter(t => {
                                        const dueDate = t.EndDate ? new Date(t.EndDate) : null;
                                        return dueDate && dueDate < now && (t.Status === "active" || !t.Completed);
                                });
                                reportLines.push(`- Overdue: ${overdueTreatments.length}`);
                                reportLines.push("");
                        }

                        if (reportType === "weekly-summary" || reportType === "calf-health") {
                                // Calf health data
                                const healthPredictions = predictions.filter(p =>
                                        p.Type === "health" || p.type === "health"
                                );

                                const periodVaccinations = vaccinations.filter(v => {
                                        const vacDate = v.Date ? new Date(v.Date) : (v.t ? new Date(v.t) : null);
                                        return vacDate && vacDate >= startDate && vacDate <= endDate;
                                });

                                const periodDiseases = diseaseRecords.filter(d => {
                                        const diseaseDate = d.Date ? new Date(d.Date) : (d.t ? new Date(d.t) : null);
                                        return diseaseDate && diseaseDate >= startDate && diseaseDate <= endDate;
                                });

                                reportLines.push("**Calf Health Status:**");
                                reportLines.push(`- Health Predictions: ${healthPredictions.length}`);
                                reportLines.push(`- Vaccinations in Period: ${periodVaccinations.length}`);
                                reportLines.push(`- Disease Records in Period: ${periodDiseases.length}`);
                                reportLines.push("");
                        }

                        if (reportType === "vet-prep") {
                                reportLines.push("**Veterinary Visit Preparation:**");
                                reportLines.push("");

                                // High priority animals. The former `HealthScore < 40` literal
                                // disagreed with the app-wide review rule (and missed animals
                                // flagged only via HealthStatus/HealthSeverity).
                                const criticalAnimals = filteredAnimals.filter((a) => requiresAnimalHealthReview(a));
                                if (criticalAnimals.length > 0) {
                                        reportLines.push("**Priority Animals (require health review):**");
                                        criticalAnimals.slice(0, 10).forEach(animal => {
                                                reportLines.push(`- ${animal.ID || animal.MAC}: ${animal.HealthScore?.toFixed(1)}%${animal.Group ? ` (${animal.Group})` : ""}`);
                                        });
                                        reportLines.push("");
                                }

                                // Recent treatments
                                const recentTreatments = treatments
                                        .filter(t => {
                                                const treatmentDate = t.StartDate ? new Date(t.StartDate) : (t.t ? new Date(t.t) : null);
                                                return treatmentDate && treatmentDate >= startDate;
                                        })
                                        .slice(0, 10);

                                if (recentTreatments.length > 0) {
                                        reportLines.push("**Recent Treatments:**");
                                        recentTreatments.forEach(treatment => {
                                                const date = formatDateLabel(treatment.StartDate || treatment.t, locale, t);
                                                const animalId = treatment.AnimalId || treatment.MAC || "Unknown";
                                                const name = treatment.TreatmentName || treatment.Name || "Treatment";
                                                reportLines.push(`- ${date}: ${animalId} - ${name}`);
                                        });
                                        reportLines.push("");
                                }

                                reportLines.push("**Discussion Points:**");
                                reportLines.push("- Review suspicious animals and recent health score trends");
                                reportLines.push("- Evaluate treatment effectiveness");
                                reportLines.push("- Review vaccination compliance and upcoming vaccinations");
                        }

                        const message = `${title}\n\n${reportLines.join("\n")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: false,
                                        reportType,
                                        focusGroup,
                                        animalCount: filteredAnimals.length,
                                        suspiciousCount,
                                        avgHealthScore: avgHealth,
                                },
                        };
                }

                case TOOL_IDS.ACTIVITY_ANOMALY_ANALYSIS: {
                        const timeframe = payload.timeframe || "48h";
                        const group = payload.group || null;
                        const anomalyType = payload.anomalyType || "all";
                        const threshold = payload.threshold || 30;

                        const animals = await getAnimals(userId);

                        // Filter by group if specified
                        const filteredAnimals = group
                                ? animals.filter(a => a.Group && a.Group.toLowerCase() === group.toLowerCase())
                                : animals;

                        const analysisLines = [];
                        const title = `📈 ${t("ai-chat-tool-activity-anomaly-analysis-title", { defaultValue: "Activity Anomaly Analysis" })}`;

                        analysisLines.push(`**Timeframe:** ${timeframe}`);
                        analysisLines.push(`**Anomaly Threshold:** ${threshold}% deviation`);
                        if (group) {
                                analysisLines.push(`**Group:** ${group}`);
                        }
                        analysisLines.push(`**Animals Analyzed:** ${filteredAnimals.length}`);
                        analysisLines.push("");

                        // Identify animals with concerning patterns based on health scores
                        // In a full implementation, this would analyze actual activity data from sensors
                        const anomalies = [];

                        filteredAnimals.forEach(animal => {
                                if (typeof animal.HealthScore !== "number") return;

                                // Simulated anomaly detection based on health score as proxy
                                // Real implementation would check actual activity patterns
                                if (animal.HealthScore < SUSPICIOUS_HEALTH_SCORE_THRESHOLD) {
                                        const severity = animal.HealthScore < 30 ? "severe" : "moderate";
                                        anomalies.push({
                                                id: animal.ID || animal.MAC,
                                                group: animal.Group || "Unassigned",
                                                healthScore: animal.HealthScore,
                                                type: "decreased",
                                                severity,
                                        });
                                }
                        });

                        // Filter by anomaly type if specified
                        const filteredAnomalies = anomalyType === "all"
                                ? anomalies
                                : anomalies.filter(a => a.type === anomalyType);

                        if (filteredAnomalies.length > 0) {
                                analysisLines.push(`**Detected Anomalies (${filteredAnomalies.length}):**`);
                                analysisLines.push("");

                                // Group by severity
                                const severe = filteredAnomalies.filter(a => a.severity === "severe");
                                const moderate = filteredAnomalies.filter(a => a.severity === "moderate");

                                if (severe.length > 0) {
                                        analysisLines.push("**Critical (Health Score <30):**");
                                        severe.slice(0, 5).forEach(a => {
                                                analysisLines.push(`- ${a.id}: ${a.healthScore.toFixed(1)}% (${a.group})`);
                                        });
                                        if (severe.length > 5) {
                                                analysisLines.push(`  ...and ${severe.length - 5} more`);
                                        }
                                        analysisLines.push("");
                                }

                                if (moderate.length > 0) {
                                        analysisLines.push("**Moderate (Health Score 30-50):**");
                                        moderate.slice(0, 5).forEach(a => {
                                                analysisLines.push(`- ${a.id}: ${a.healthScore.toFixed(1)}% (${a.group})`);
                                        });
                                        if (moderate.length > 5) {
                                                analysisLines.push(`  ...and ${moderate.length - 5} more`);
                                        }
                                        analysisLines.push("");
                                }

                                analysisLines.push("**Recommendations:**");
                                if (severe.length > 0) {
                                        analysisLines.push("- Investigate critical animals immediately");
                                }
                                if (severe.length >= 3) {
                                        analysisLines.push("- Multiple severe anomalies may indicate environmental issue or disease outbreak");
                                }
                                analysisLines.push("- Use animal-details tool for individual investigation");
                        } else {
                                analysisLines.push("**No significant anomalies detected.**");
                                analysisLines.push("");
                                analysisLines.push("All animals within normal activity parameters for the specified timeframe.");
                        }

                        const message = `${title}\n\n${analysisLines.join("\n")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: filteredAnomalies.length === 0,
                                        timeframe,
                                        group,
                                        anomalyType,
                                        anomalyCount: filteredAnomalies.length,
                                        analyzedCount: filteredAnimals.length,
                                },
                        };
                }

                case TOOL_IDS.FEEDING_ANALYSIS: {
                        const sensorId = payload.sensorId || null;
                        const timeframe = payload.timeframe || "7d";
                        const alertThreshold = payload.alertThreshold || 30;
                        const group = payload.group || null;

                        // Get sensors (BioFlow water meters)
                        const allNodes = await getAllNodes(userId);
                        const bioflowSensors = allNodes.filter(node =>
                                node.Type === "BioFlow" || node.type === "bioflow" ||
                                node.SensorType === "water" || node.sensorType === "water"
                        );

                        const analysisLines = [];
                        const title = `💧 ${t("ai-chat-tool-feeding-analysis-title", { defaultValue: "Feeding & Water Analysis" })}`;

                        analysisLines.push(`**Timeframe:** ${timeframe}`);
                        analysisLines.push(`**Alert Threshold:** ${alertThreshold}% deviation`);
                        if (sensorId) {
                                analysisLines.push(`**Sensor:** ${sensorId}`);
                        }
                        if (group) {
                                analysisLines.push(`**Group:** ${group}`);
                        }
                        analysisLines.push("");

                        if (bioflowSensors.length === 0) {
                                analysisLines.push("**No BioFlow water sensors found.**");
                                analysisLines.push("");
                                analysisLines.push("Water consumption tracking requires BioFlow sensors to be installed and configured.");
                                analysisLines.push("");
                                analysisLines.push("**Alternative Monitoring:**");
                                analysisLines.push("- Monitor health scores for signs of dehydration (activity decline)");
                                analysisLines.push("- Check for reduced feed intake via animal behavior patterns");
                                analysisLines.push("- Use suspicious-animals tool to identify animals needing attention");
                        } else {
                                analysisLines.push(`**BioFlow Sensors Available:** ${bioflowSensors.length}`);
                                analysisLines.push("");

                                // List sensors
                                bioflowSensors.slice(0, 5).forEach(sensor => {
                                        const sensorName = sensor.Name || sensor.ID || sensor.MAC || "Unnamed Sensor";
                                        const status = sensor.Connected || sensor.Active ? "Connected" : "Offline";
                                        analysisLines.push(`- ${sensorName}: ${status}`);
                                });

                                if (bioflowSensors.length > 5) {
                                        analysisLines.push(`  ...and ${bioflowSensors.length - 5} more sensors`);
                                }

                                analysisLines.push("");
                                analysisLines.push("**Note:** Detailed consumption analysis requires sensor data retrieval.");
                                analysisLines.push("For specific water consumption patterns, check the BioFlow dashboard in the app.");
                                analysisLines.push("");
                                analysisLines.push("**Feeding Health Indicators:**");
                                analysisLines.push("- Sudden drops (>30%) in water consumption often indicate illness");
                                analysisLines.push("- Temperature affects intake: expect reduced consumption in hot weather");
                                analysisLines.push("- Calves require 5-10% of body weight in water daily; lactating cows need 70-120+ liters/day");
                        }

                        const message = `${title}\n\n${analysisLines.join("\n")}`;

                        return {
                                message,
                                metadata: {
                                        toolId,
                                        payload,
                                        empty: bioflowSensors.length === 0,
                                        timeframe,
                                        sensorId,
                                        group,
                                        sensorCount: bioflowSensors.length,
                                },
                        };
                }

                default:
                        throw new Error(`Unsupported chat tool: ${toolId}`);
        }
}

export const CHAT_TOOL_IDS = TOOL_IDS;
function formatTokenPreview(token, t) {
        if (!token) {
                return t("ai-chat-field-not-available");
        }

        const cleaned = String(token).trim();
        if (cleaned.length <= 12) {
                return cleaned;
        }

        return t("ai-chat-field-device-token-preview", {
                start: cleaned.slice(0, 6),
                end: cleaned.slice(-4),
        });
}

